/**
 * AI BF (Baseline Forecasting) Scenario Planning
 * BF 모듈 전용 - 마케팅 신호 사이드 패널 통합 분할 화면
 *
 * BF 시나리오는 엔진 실행 없이 외부 인자(feature) 추천을 수행합니다.
 *    - 전체 예측 실행은 수 시간 소요 → saas-ai Spark 환경 필요
 *    - 마케팅 신호와 현재 MAPE 분포를 분석하여 추가할 feature를 AI가 추천합니다.
 *    - 우측 패널: 승인된 마케팅 신호를 보여주고 BF feature로 연계 가능한 신호를 선택합니다.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "@plannel/utils/i18n";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Collapse,
  Grid, IconButton, LinearProgress, Paper, Snackbar, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import dpProcessMgmtService from "@plannel/services/dp/dp-process-mgmt-service";
import ArrowBackIcon    from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TrendingUpIcon    from "@mui/icons-material/TrendingUp";
import TrendingDownIcon  from "@mui/icons-material/TrendingDown";
import RemoveIcon        from "@mui/icons-material/Remove";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import ExpandLessIcon    from "@mui/icons-material/ExpandLess";
import CampaignIcon      from "@mui/icons-material/Campaign";
import NewspaperIcon     from "@mui/icons-material/Newspaper";
import ForumIcon         from "@mui/icons-material/Forum";
import BarChartIcon      from "@mui/icons-material/BarChart";
import SmartToyIcon      from "@mui/icons-material/SmartToy";
import InventoryIcon     from "@mui/icons-material/Inventory";
import GavelIcon         from "@mui/icons-material/Gavel";
import StorefrontIcon    from "@mui/icons-material/Storefront";
import AutoAwesomeIcon   from "@mui/icons-material/AutoAwesome";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RefreshIcon       from "@mui/icons-material/Refresh";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import LanguageIcon      from "@mui/icons-material/Language";
import scenarioService from "../../services/ai/scenarioService";
import { rememberModuleCatalog } from "./scenarioParamUtil";
import CollapsibleText from "./CollapsibleText";
import { StatusSection, StatusKpiSection, NavName, StatusSparkline, StatusEmpty,
         StatusEmptyPanel, StatusPendingTag, buildStatusPanelSummary,
         openPlanRunScreen, useLastRunSummary,
         StatusItemHeader, StatusItemRow, StatusItemList, statusListH } from "./scenarioStatusUi";
import {
  ResponsiveContainer, ComposedChart, Bar, Cell, Line, Customized,
  XAxis, YAxis, CartesianGrid, Legend, Tooltip as RTooltip,
} from "recharts";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { C as AI_C } from "@plannel/components/ai/aiTokens";
import { paramRowSx, PARAM_ROW_PAD_X, PARAM_NUM_INPUT_W,
         PARAM_SELECT_W, PARAM_ROW_MIN_H } from "./scenarioParamUtil";
import ScenarioParamAddPicker from "./ScenarioParamAddPicker";
import { ParamValueCells, ParamRevertButton, useEditEndGuard } from "./scenarioParamCells";
import { fieldText, tKeyOr } from "./aiFieldText";

// ── BF 모듈 설정 ──────────────────────────────────────────────────────────
// KPI 메타 — col b 만 시나리오 종류에 따라 달라진다.
//   기본(계절성·외부인자): "현행 대비 개선"(MAPE %p)
//   마케팅 신호(scn_bf_003): "미래 수요 변화"(신호 반영 범위별 미래 수요 델타 %)
const BF_KPI_A = {
  key: "a", icon: "", label: "bfKpiRepItemAvgMapeLabel",
  getStatus: (v) => {
    if (v === "-" || v === "—") return "neutral";
    const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
    if (isNaN(n)) return "neutral";
    return n <= 15 ? "good" : n <= 30 ? "near" : "warn";
  },
  insight: "bfKpiRepItemAvgMapeInsight",
  note: "lowerIsBetterNote",
};
const BF_KPI_B_DEFAULT = {
  key: "b", icon: "", label: "bfKpiImprovementVsCurrentLabel",
  getStatus: (v) => {
    if (v === "-" || v === "—") return "neutral";
    const n = parseFloat(String(v).replace(/[^0-9.+-]/g, ""));
    if (isNaN(n)) return "neutral";
    return n > 0 ? "good" : n < 0 ? "warn" : "neutral";
  },
  insight: "bfKpiImprovementVsCurrentInsight",
  note: "bfImprovementNote",
};
// 편향(Bias) — WAPE(오차 크기)와 짝을 이루는 두 번째 품질 축. 표시값은 부호 유지(+과대/−과소),
//   kpisRaw.f 는 |bias| 라 최적 판정(min)·트레이드오프 x좌표가 "0에 가까울수록 좋음"으로 동작.
const BF_KPI_F = {
  key: "f", icon: "", label: "bfKpiBiasLabel",
  desc: "bfKpiBiasDesc",   // 라벨 밑 한 줄 설명 — 방향(+/−)의 업무 의미(과잉재고/결품 위험)
  getStatus: (v) => {
    if (v === "-" || v === "—") return "neutral";
    const n = parseFloat(String(v).replace(/[^0-9.+-]/g, ""));
    if (isNaN(n)) return "neutral";
    const a = Math.abs(n);
    return a <= 10 ? "good" : a <= 25 ? "near" : "warn";
  },
  insight: "bfKpiBiasInsight",
  note: "bfKpiBiasInsight",
};
// (대표품목 수 행(key "c")은 모든 시나리오가 동일 값이라 KPI 상세 비교에서 제외 — 2026-07)
// (허용 알고리즘 수 행(key "d")은 구성 팝오버의 알고리즘 목록과 중복이라 제거 — 2026-08-14)
// ── 상세 분포 KPI (2026-08-14) — 평균 뒤에 숨은 품목 분포. 백엔드가 공통 모수로 산출 ──
const _bfNeutral = () => "neutral";
const BF_KPI_G = {   // 개선 품목 수 (현행 대비) — 평균 개선이 고른 개선인지 구분
  key: "g", icon: "", label: "bfKpiImprovedItemsLabel",
  getStatus: _bfNeutral, insight: "bfKpiImprovedItemsLabel", note: "bfKpiImprovedItemsLabel",
};
const BF_KPI_H = {   // 악화 품목 수 (현행 대비)
  key: "h", icon: "", label: "bfKpiWorsenedItemsLabel",
  getStatus: _bfNeutral, insight: "bfKpiWorsenedItemsLabel", note: "bfKpiWorsenedItemsLabel",
};
const BF_KPI_I = {   // 오차 50% 초과 품목 수 — 현황 분석 '주의' 기준과 동일 잣대
  key: "i", icon: "", label: "bfKpiHighErrorItemsLabel",
  getStatus: _bfNeutral, insight: "bfKpiHighErrorItemsLabel", note: "bfKpiHighErrorItemsLabel",
};
const BF_KPI_J = {   // 품목 오차 중앙값 — 물량가중 평균(WAPE)을 보완하는 '보통 품목' 체감 오차
  key: "j", icon: "", label: "bfKpiMedianErrorLabel",
  desc: "bfKpiMedianErrorDesc",   // 라벨 밑 한 줄 설명 — 평균(WAPE)과의 차이
  getStatus: _bfNeutral, insight: "bfKpiMedianErrorLabel", note: "bfKpiMedianErrorLabel",
};
const BF_KPI_K = {   // 과대·과소예측 품목 수 — 편향 평균의 상쇄 여부 노출
  key: "k", icon: "", label: "bfKpiBiasSplitLabel",
  getStatus: _bfNeutral, insight: "bfKpiBiasSplitLabel", note: "bfKpiBiasSplitLabel",
  noDelta: true,   // 합성 값이라 Δ 배지 수치 파싱이 무의미 — 억제
  // "+25 · -12" 원문 대신 "과대 25 · 과소 12"(라벨 뮤트·숫자 강조)로 렌더 — 가독성.
  //   kpisRaw.k_over/k_under 우선, 구 응답은 문자열 파싱 폴백. 실패 시 null → 기본 표시.
  renderValue: (sc, t) => {
    const raw = sc?.kpisRaw || {};
    let over = raw.k_over, under = raw.k_under;
    if (over == null || under == null) {
      const m = /^\+?(\d+)\s*·\s*-(\d+)$/.exec(String((sc?.kpis || {}).k || "").trim());
      if (m) { over = Number(m[1]); under = Number(m[2]); }
    }
    if (over == null || under == null) return null;
    const part = (lblKey, n) => (
      <Box key={lblKey} component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: "3px" }}>
        <Box component="span" sx={{ fontSize: FONT.MICRO, color: AI_C.text3, fontWeight: 600 }}>{t(lblKey)}</Box>
        <Box component="span" sx={{ fontSize: FONT.SUB, color: AI_C.text1, fontWeight: 700,
          fontVariantNumeric: "tabular-nums" }}>{n}</Box>
      </Box>
    );
    return (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline",
        gap: "10px", whiteSpace: "nowrap" }}>
        {part("bfOverForecastShort", over)}
        {part("bfUnderForecastShort", under)}
      </Box>
    );
  },
};
const BF_KPI_E = {
  key: "e", icon: "", label: "bfKpiAppliedSignalCountLabel",
  getStatus: () => "neutral",
  insight: "bfKpiAppliedSignalCountInsight",
  note: "bfMarketingSignalNote",
  // 셀 부가 렌더 — 반영 신호 제목 칩. 별도 "적용 시장신호" 섹션 대신 KPI 행에서 바로 확인.
  cellExtra: (sc, t) => {
    const sigs = sc.applied_signals || [];
    if (!sigs.length) return null;
    const arrow = (d, n) => ((d && /up|증가|상승|positive/i.test(String(d))) || n > 0) ? "▲" : "▼";
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, mt: 0.5 }}>
        {sigs.map((sig, i) => {
          const pct = Number(sig.demand_impact_pct || 0);
          return (
            <Tooltip key={sig.id ?? i} title={sig.title || ""} placement="top" arrow>
              <Chip size="small"
                label={`${arrow(sig.direction, pct)} ${sig.title || t("bfSignalOrdinalLabel", { n: i + 1 })} (${pct > 0 ? "+" : ""}${pct.toFixed(0)}%)`}
                sx={{ height: 18, fontSize: FONT.MICRO, maxWidth: "100%",
                      bgcolor: C.bg2, color: C.text2, border: `1px solid ${C.border}` }} />
            </Tooltip>
          );
        })}
      </Box>
    );
  },
};
function bfKpiMeta(_tplId) {
  // 신호 후보정 시나리오도 동일 품목 백테스트라 모든 시나리오를 '현행 대비 개선'(MAPE)으로 비교.
  // 허용 알고리즘 수(D)·반영 신호 수(E)는 항상 별도 행(혼합 시나리오에서도 의미가 안 섞이게).
  return [BF_KPI_A, BF_KPI_F, BF_KPI_B_DEFAULT,
          BF_KPI_G, BF_KPI_H, BF_KPI_I, BF_KPI_J, BF_KPI_K, BF_KPI_E];
}

export const BF_MODULE = {
  code: "BF",
  _activeTplId: null,
  lockBase: true,   // base(현황 유지)는 현재 설정 그대로 — 사용자 수정 불가

  // 흐름도 배선 — 부모 래퍼 단계가 없어 분기 앵커를 합성한다.
  //   시나리오 아래 품목 자식(_item_)은 한 행으로 접는다.
  //   flowTopNodes = 공통 4(현황·대표품목 선정·기준 확정·KPI 해석) + 합성 앵커 1.
  engineTrack: { parentTool: null, childPrefix: "bf_scn_", match: "role",
                 itemSplit: "_item_", flowTopNodes: 5 },

  // ── Analysis 탭 Quick Prompt (자유 질문, 액션 없음) — { i18nKey, ko } ──────────
  // ko = l10n 미등록 시 폴백 라벨. 다국어는 translation.*.json 의 plannelAgent 키로 마지막에 채운다.
  quickPrompts: {
    analysis: {
      0: [
        { i18nKey: "agentAskBfAccuracy", ko: "현황 예측 정확도는?" },
        { i18nKey: "agentAskBfProblem",  ko: "어떤 항목이 문제?" },
        { i18nKey: "agentAskBfImprove",  ko: "개선 방향은?" },
      ],
      1: [
        { i18nKey: "agentAskBfFeature", ko: "어떤 외부 인자가 좋아?" },
        { i18nKey: "agentAskBfSignal",  ko: "마케팅 신호 영향은?" },
      ],
      2: [
        { i18nKey: "agentAskBfBest",    ko: "어떤 시나리오가 최적이야?" },
        { i18nKey: "agentAskBfCompare", ko: "정확도 개선 비교" },
      ],
      3: [
        { i18nKey: "agentAskBfChecklist", ko: "확정 전 체크리스트" },
      ],
    },
  },

  // ── Action 탭: 화면 하위상태(step·mode·aiResult)에 맞는 "지금 가능한 액션"만 선언 (DP 와 동일 패턴) ──
  // BF 도 다른 모듈과 동일하게 표준 시나리오(templates) + 파라미터(params) 3열 편집기를 사용한다.
  // (AI 추천 "생성/재생성"은 startAiAnalysis 를 가진 StepScenarioCreate 가 별도로 등록한다.)
  // description/confirmKo 는 모듈 레벨 함수(컴포넌트 아님)라 useTranslation 훅을 쓸 수 없어
  //   i18n 싱글턴(i18n.t)으로 미리 번역해 둔다 (IP/DP/RP agentActions 와 동일 패턴).
  agentActions: ({ step, mode, aiResult, exec }) => {
    const selMode = (m, key, ko) => {
      const modeLabel = i18n.t(key, { defaultValue: ko });
      return {
        id: `bf.mode.${m}`, i18nKey: key, ko, group: "action", style: "secondary",
        description: i18n.t("ipSelectModeDesc", { mode: modeLabel }),
        run: () => exec({ action: "select_mode", payload: { mode: m } }),
        confirmKo: i18n.t("ipSelectModeConfirm", { mode: modeLabel }),
      };
    };
    const runEngine = {
      id: "bf.engine.run", i18nKey: "agentActBfRunEngine", ko: "엔진 실행",
      group: "action", style: "primary",
      description: i18n.t("bfRunEngineDesc"),
      run: () => exec({ action: "run_engine" }),
      confirmKo: i18n.t("MSG_BfEngineRunConfirm"),
    };
    if (step === 0) return [
      selMode("ai",     "agentActBfModeAi",     "AI 추천 시나리오 생성"),
      selMode("custom", "agentActBfModeCustom", "표준 시나리오 선택"),
    ];
    if (step === 1) {
      // mode 미설정(null)은 표준 시나리오 선택 상태와 동일(통합 뷰 기본) — 초기 방식 선택 액션 없이
      //   표준 경로 액션을 그대로 노출. AI 전환은 컨테이너 공통 scn.mode.ai 가 제공.
      // 방식=ai 인데 생성 전 → 생성 액션(StepScenarioCreate)만 노출
      if (mode === "ai" && !aiResult) return [];
      return [runEngine];
    }
    return [];   // 단계 이동은 컨테이너 generic nav(nav.step*)가 전담 — 모듈별 고정 navTo 는 라벨 중복이라 제거
  },
  // label/step2lbl/step4lbl/recommendLabel/recommendDesc/initInputs/outputs 값은 i18n 키 문자열 —
  //   모듈 레벨 상수라 t()를 직접 쓸 수 없으므로 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
  //   confirmDone 은 함수/객체라 소비처가 t()로 감싸지 않으므로 i18n 싱글턴으로 미리 번역한다.
  label: "bfModuleLabel",
  Icon: ShowChartIcon,
  step2lbl: "bfStep2Label",
  step4lbl: "bfStep4Label",
  inputFrom: null,

  // recommendOnly: true → 엔진 시뮬 없이 AI 인자 추천 + 선택 인자 실제 BF 시뮬 KPI 비교
  recommendOnly: true,

  // 시나리오 비교 탭 '리포트' 버튼 활성화 (의사결정 PDF 리포트). 현재 BF 만 백엔드 지원.
  reportEnabled: true,
  recommendLabel: "bfRecommendLabel",
  recommendDesc: "bfRecommendDesc",

  initInputs: ["bfInitInputSalesHistory", "bfInitInputLeaderboardMape", "bfInitInputMarketSignal", "bfInitInputModelConfig"],

  // BF 확정 = 설정 반영(엔진 미실행). 완료 카드는 'DP 시작'(다음 모듈)이 아니라
  //   "BF 실행 필요" 안내 + 수요계획 프로세스 관리(버전 생성 시 BF 자동 실행)로 이동.
  confirmDone: {
    title: (scName) => i18n.t("bfConfirmDoneTitle", { scName }),
    desc: i18n.t("bfConfirmDoneDesc"),
    ctaLabel: i18n.t("bfConfirmDoneCtaLabel"),
    ctaMenuKey: 3100,   // TabMenuList: DP_PROCESS_MGMT (수요계획 프로세스 관리)
  },

  // scenario.kind 에 따라 BfCustomScenarioFields 가 시나리오별로 다른 파라미터를 렌더한다.
  //   seasonality     → 대상 제품군 · 예측 알고리즘(다중) · 에러율 평가 버킷 수
  //   external_factor → 외부 인자(다중) · 예측 알고리즘(RF/XGB 고정 활성) · 인자 영향도 임계값
  scenarios: [
    {
      role: "base", name: "scnBfBaseName", kind: "seasonality",
      desc: "scnBfBaseDesc",
      tags: ["scnTagKeepCurrent"],
      params: { target_group: "all", algorithms: ["AR", "HW", "PR", "XGB", "RF"], history_horizon: 3 },
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-" },
    },
    {
      role: "alt1", name: "scnBfAlt1Name", kind: "seasonality",
      desc: "scnBfAlt1Desc",
      tags: ["scnTagSeasonalityBoost", "scnTagAlgoLimited"],
      params: { target_group: "seasonal", algorithms: ["AR", "HW", "PR"], history_horizon: 12 },
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-" },
    },
    {
      role: "alt2", name: "scnBfAlt2Name", kind: "external_factor",
      desc: "scnBfAlt2Desc",
      tags: ["scnTagExternalFactor", "scnTagOilRawMaterial"],
      // 알고리즘 기본값 = 트리 모델만 — 외부 인자는 RF/XGB 만 feature 로 학습할 수 있어
      // 전체 알고리즘을 시드하면 "표시는 전부인데 실행은 RF/XGB"로 어긋난다 (2026-07-15).
      // (baseline(인자 전) 비교도 같은 풀을 쓰므로 트리 한정이 인자 효과를 더 깨끗하게 분리)
      params: { external_factors: ["oil", "raw"], algorithms: ["XGB", "RF"], influence_threshold: 0.7 },
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-" },
    },
  ],
  // 렌더는 mod.CustomScenarioFields(시나리오별) 가 담당 → generic params 미렌더.
  // 단, 챗 set_param 의 executeAction 방어검증(mod.params 에서 field spec 조회)을 통과시키려면
  // 백엔드 BF_PARAM_SCHEMA 와 동일한 단일값 필드 스펙이 필요하다. vals 미지정 → seedScenarios 백필 영향 없음.
  params: [
    { paramKey: "target_group", type: "select", label: "bfTargetGroupLabel", options: [
        { value: "all", label: "all" },
        { value: "seasonal", label: "bfSeasonalGroupLabel" },
        { value: "classA", label: "bfClassAItemLabel" },
    ] },
    { paramKey: "history_horizon", type: "number", label: "bfErrorEvalBucketCountLabel" },
    { paramKey: "influence_threshold", type: "number", label: "bfFactorInfluenceThresholdLabel" },
    { paramKey: "signal_scope", type: "select", label: "bfSignalScopeLabel", options: [
        { value: "none", label: "bfSignalScopeNoneLabel" },
        { value: "partial", label: "bfSignalScopePartialLabel" },
        { value: "all", label: "bfSignalScopeAllLabel" },
    ] },
    // 배열·객체형 파라미터 — BF 카드는 전용 위젯이 렌더하지만, 공용 소비처(커스텀 시나리오 저장 팝업 등)는
    //   여기 라벨·format 이 없으면 키 원문과 "AR,HW,PR"·"[object Object]" 를 그대로 노출한다.
    //   options/format 은 아래에서 정의되는 상수(BF_ALGO_OPTS 등)를 참조하므로 지연 평가(getter·함수)한다.
    { paramKey: "algorithms", type: "multi", label: "forecastAlgorithmList",
      get options() { return BF_ALGO_OPTS.map(o => ({ value: o.v, label: o.l })); },
      format: (v) => (Array.isArray(v) ? v.map(x => _bfLbl(BF_ALGO_OPTS, x)).join(" · ") : null) },
    { paramKey: "external_factors", type: "multi", label: "bfExternalFactorLabel",
      get options() { return BF_FACTOR_OPTS_ALL.map(o => ({ value: o.v, label: o.l })); },
      format: (v) => (Array.isArray(v) ? v.map(x => _bfLbl(BF_FACTOR_OPTS_ALL, x)).join(" · ") : null) },
    { paramKey: "factor_future", label: "bfFutureFactorApplyMethodLabel",
      format: (v, t) => (v && typeof v === "object" && !Array.isArray(v)
        ? Object.entries(v).map(([k, cfg]) => `${_bfLbl(BF_FACTOR_OPTS_ALL, k)} ${
            t(FACTOR_METHOD_LABEL[cfg?.method] || FACTOR_METHOD_LABEL[FACTOR_METHOD_DEFAULT])}`).join(" · ")
        : null) },
    // 신호 ID 는 내부 식별자라 노출하지 않는다 — 적용 여부만 표시.
    { paramKey: "signal_ids", label: "bfMarketingSignalApplyLabel",
      format: (v, t) => t(Array.isArray(v) && v.length ? "paramToggleOnLabel" : "paramToggleOffLabel") },
    // BF Settings 계열 파라미터 (2026-08-14) — 라벨·옵션은 BF Settings 화면과 동일 키 재사용.
    //   비교 화면 '시나리오 구성'(base와 달라진 설정)과 챗 set_param 방어검증이 이 스펙을 참조한다.
    { paramKey: "eval_metric", type: "select", label: "metrics",
      get options() { return BF_METRIC_OPTS.map(o => ({ value: o.v, label: o.l })); } },
    { paramKey: "remove_outlier", type: "select", label: "removeOutlier",
      get options() { return BF_OUTLIER_OPTS.map(o => ({ value: o.v, label: o.l })); } },
    { paramKey: "outlier_sensitivity", type: "number", label: "sensitivity" },
    { paramKey: "outlier_method", type: "select", label: "method",
      get options() { return BF_OUTLIER_METHOD_OPTS.map(o => ({ value: o.v, label: o.l })); } },
    { paramKey: "train_months", type: "number", label: "trainingPeriod" },
  ],
  // 커스텀 시나리오 저장 시 params 에 함께 실을 비편집 값 — BF 는 kind(시나리오 종류).
  //   kind 는 사용자 파라미터가 아니라 판별자라 저장 팝업 목록에는 노출하지 않지만,
  //   저장본에서 빠지면 재조회 시 _bindBfVariant 가 kind 를 못 찾아 카드가 seasonality 로 열리고
  //   외부인자·마케팅 신호 필드가 편집 불가로 사라진다(값은 params 에 남아 엔진에는 계속 전달).
  customSaveExtraParams: (sc) => (sc?.kind ? { kind: sc.kind } : null),
  skipKpiSection: true,   // BfExtraInitContent에서 실제 현황 데이터를 이미 표시하므로 KPI 요약 카드 숨김
  // "c"(대표품목 수)는 전 시나리오 동일 값, "d"(허용 알고리즘 수)는 구성 팝오버의
  //   알고리즘 목록과 중복이라 표·팝오버 모두에서 제외(2026-08-14).
  //   g~k = 상세 분포 KPI(개선/악화/50%초과/중앙값/과대·과소 품목 수).
  kpiKeys:   ["a", "f", "b", "g", "h", "i", "j", "k", "e"],
  // d(허용 알고리즘 수)·e(반영 신호 수)는 성과 KPI가 아닌 입력 조건 — KPI 상세 표에서 제외.
  //   d 는 구성 팝오버의 '예측 알고리즘' 목록과 중복이라 표시하지 않는다(2026-08-14).
  //   e(반영 신호 수)만 구성 팝오버에 표시 — 신호 시나리오가 없으면 자동 숨김.
  configKpiKeys: ["e"],
  // 최적 판정 방향 — a(WAPE)·f(편향, raw=|bias|)·h(악화)·i(50%초과)·j(중앙값)는 작을수록,
  //   g(개선 품목 수)는 클수록 좋음. b는 신호 템플릿에서 의미가 바뀌므로(미래 수요 변화)
  //   방향 미지정 유지 → bestMap 폴백. k(과대·과소 분포)는 방향성 없는 정보성 행.
  kpiDir: { a: "min", f: "min", g: "max", h: "min", i: "min", j: "min" },
  bestMap:   { a: "alt1", b: "alt1", c: "alt1", d: "alt1" },
  // kpiLabels/kpiMeta 는 선택한 시나리오 종류(_activeTplId)에 따라 달라진다.
  //   마케팅 신호 시나리오(scn_bf_003)는 신호가 과거 정확도를 바꾸지 않으므로
  //   col b 를 "미래 수요 변화"(신호 반영 범위별 차이)로 보여준다.
  get kpiMeta()   { return bfKpiMeta(this._activeTplId); },
  get kpiLabels() { return bfKpiMeta(this._activeTplId).map(m => m.label); },
  // enginePhases 값은 i18n 키 문자열 — 현재 소비처 없음(mod.enginePhases 미사용). 향후 대비 키만 등록(IP/DP/RP 와 동일 패턴).
  enginePhases: [
    "bfEnginePhaseSelectRepItems",
    "bfEnginePhaseBacktestRun",
    "bfEnginePhaseMapeAggregate",
    "bfEnginePhaseSelectBest",
    "complete",
  ],
  aiRec: 1,

  // AI 추천 시나리오 안내 — BF는 과거 판매실적·예측정확도·승인 마케팅 신호만 분석(재고 현황 미사용),
  //   예측에 반영할 외부 인자(수요 배율·계절성·거시지표) 시나리오를 추천한다.
  aiRecCardDesc: "bfAiRecCardDesc",
  aiRecIntroLines: [
    "bfAiRecIntroLine1",
    "bfAiRecIntroLine2",
  ],
  aiRecFactors: ["bfAiRecFactorSalesActual", "bfAiRecFactorForecastAccuracy", "bfAiRecFactorModelConfig", "dpAiRecFactorApprovedMarketing"],
  aiRecLoadingDesc: "bfAiRecLoadingDesc",
  aiRecButtonLabel: "agentActBfModeAi",
};

// ── 색상 팔레트 ───────────────────────────────────────────────────────────────
const C = {
  primary: "#185FA5", primaryBg: "#E6F1FB", primaryL: "#B5D4F4",
  success: "#1D9E75", successBg: "#E1F5EE",
  warning: "#BA7517", warningBg: "#FAEEDA",
  danger:  "#E24B4A", dangerBg:  "#FCEBEB",
  neutral: "#6B7280", neutralBg: "#F3F4F6",
  purple:  "#7c3aed", purpleBg:  "#f5f3ff",
  teal:    "#0891b2", tealBg:    "#e0f2fe",
  border:  "#E5E7EB", bg:        "#F8F9FB",
  // 공통 시나리오 플래닝 팔레트(acc/g1/g2/...) — BfComposeRun 등 공통 컴포넌트 스타일과 일치
  acc:    "#185FA5", accL:  "#E6F1FB", accM:  "#378ADD", accS:  "#B5D4F4", accT:  "#0C447C",
  g1:     "#1D9E75", g1l:   "#E1F5EE", g1t:   "#085041", g1s:   "#5DCAA5",
  g2:     "#BA7517", g2l:   "#FAEEDA", g2t:   "#633806", g2s:   "#EF9F27",
  g3:     "#E24B4A", g3l:   "#FCEBEB", g3t:   "#791F1F",
  bg2:    "#FFFFFF", bg3:   "#F1F4F8",
  text1:  "#111827", text2: "#4B5563", text3: "#9CA3AF",
};

// 시나리오 role 색상/라벨 (BfComposeRun 용 — 공통 화면과 동일 매핑)
const ROLE_COLOR = { base: C.acc,  alt1: C.g1,  alt2: C.g2  };
// role → 시나리오 번호(공통 AiScenarioPlanning.js 의 roleLabel() 과 동일 규칙). base는 "기준 시나리오"(i18n).
const ROLE_SCEN_NO = { alt1: 1, alt2: 2, s1: 1, s2: 2 };
const roleLabelText = (role, t) => (role === "base" ? t("baseScenarioLabel") : (ROLE_SCEN_NO[role] != null ? t("scenarioNumber", { n: ROLE_SCEN_NO[role] }) : role));

// BF 외부 인자 유형 메타 (인자 선택 카드 표시용) — label 값은 i18n 키 문자열, 소비처(컴포넌트)에서 t()로 변환.
const BF_FEATURE_META = {
  demand_multiplier: { label: "bfFeatureDemandMultiplierLabel",   clr: C.acc, bg: C.accL, bdr: C.accS, txt: C.accT },
  seasonal_index:    { label: "bfFeatureSeasonalIndexLabel", clr: C.teal, bg: "#e0f2fe", bdr: "#7dd3fc", txt: "#075985" },
  macro_indicator:   { label: "bfFeatureMacroIndicatorLabel", clr: "#7c3aed", bg: "#f5f3ff", bdr: "#ddd6fe", txt: "#5b21b6" },
  trend_strength:    { label: "bfFeatureTrendStrengthLabel",   clr: C.g1, bg: C.g1l, bdr: C.g1s, txt: C.g1t },
  supply_risk_score: { label: "bfFeatureSupplyRiskLabel", clr: C.g2, bg: C.g2l, bdr: C.g2s, txt: C.g2t },
};

// ── 신호 타입 아이콘 ──────────────────────────────────────────────────────────
const SIG_ICON = {
  news:       <NewspaperIcon   sx={{ fontSize: 12 }} />,
  sns:        <ForumIcon       sx={{ fontSize: 12 }} />,
  sales:      <BarChartIcon    sx={{ fontSize: 12 }} />,
  forecast:   <SmartToyIcon    sx={{ fontSize: 12 }} />,
  internal:   <CampaignIcon    sx={{ fontSize: 12 }} />,
  history:    <BarChartIcon    sx={{ fontSize: 12 }} />,
  channel:    <StorefrontIcon  sx={{ fontSize: 12 }} />,
  supplier:   <InventoryIcon   sx={{ fontSize: 12 }} />,
  market:     <NewspaperIcon   sx={{ fontSize: 12 }} />,
  regulation: <GavelIcon       sx={{ fontSize: 12 }} />,
  contract:   <CheckCircleIcon sx={{ fontSize: 12 }} />,
  competitor: <StorefrontIcon  sx={{ fontSize: 12 }} />,
  inventory:  <InventoryIcon   sx={{ fontSize: 12 }} />,
  trend:      <LanguageIcon    sx={{ fontSize: 12 }} />,
  web:        <LanguageIcon    sx={{ fontSize: 12 }} />,
};

// ── feature_type → 색상 ───────────────────────────────────────────────────
const FEATURE_COLOR = {
  demand_multiplier: C.primary,
  trend_strength:    C.success,
  seasonal_index:    C.teal,
  macro_indicator:   C.purple,
  supply_risk_score: C.warning,
};

// ── 방향 → 아이콘·색상 ────────────────────────────────────────────────────
function DirectionBadge({ dir }) {
  if (dir === "up")        return <TrendingUpIcon   sx={{ fontSize: 14, color: C.success }} />;
  if (dir === "down")      return <TrendingDownIcon sx={{ fontSize: 14, color: C.danger  }} />;
  return                          <RemoveIcon       sx={{ fontSize: 14, color: C.neutral }} />;
}

// ── BF Feature 태그 ────────────────────────────────────────────────────────
function FeatureTag({ signal }) {
  // demand_impact 방향에 따라 적합한 feature_type 추천
  const dir = signal.direction;
  const ft = dir === "up" || dir === "down" ? "demand_multiplier"
             : signal.signal_type === "market" ? "macro_indicator"
             : "trend_strength";
  const color = FEATURE_COLOR[ft] || C.neutral;
  return (
    <Chip
      label={ft.replace(/_/g, " ")}
      size="small"
      sx={{
        height: 20, fontSize: FONT.CAPTION, fontWeight: 700,
        bgcolor: `${color}15`, color,
        border: `1px solid ${color}30`,
      }}
    />
  );
}

// ── 개별 신호 카드 ─────────────────────────────────────────────────────────
function SignalCard({ signal, selected, onToggle }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dPct = Number(signal.demand_impact_pct || 0);
  const icon = SIG_ICON[signal.signal_type] || SIG_ICON.news;
  const dColor = dPct > 0 ? C.success : dPct < 0 ? C.danger : C.neutral;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        border: selected ? `1.5px solid ${C.primary}` : `1px solid ${C.border}`,
        bgcolor: selected ? C.primaryBg : "#fff",
        cursor: "pointer",
        transition: "all 0.15s",
        "&:hover": { borderColor: C.primary, boxShadow: `0 0 0 2px ${C.primaryL}` },
      }}
      onClick={() => onToggle(signal.id)}
    >
      <CardContent sx={{ p: "8px 10px !important" }}>
        {/* 헤더 */}
        <Stack direction="row" spacing={0.6} alignItems="flex-start">
          <Box sx={{ color: C.neutral, flexShrink: 0, mt: "2px" }}>{icon}</Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontSize={11.5} fontWeight={selected ? 700 : 500}
              lineHeight={1.35} noWrap>
              {signal.title}
            </Typography>
            <Stack direction="row" spacing={0.5} mt={0.3} flexWrap="wrap" useFlexGap>
              <Stack direction="row" spacing={0.3} alignItems="center">
                <DirectionBadge dir={signal.direction} />
                <Typography variant="caption" fontSize={10} fontWeight={700}
                  sx={{ color: dColor }}>
                  {t("bfDemandPctLabel", { sign: dPct > 0 ? "+" : "", pct: dPct.toFixed(0) })}
                </Typography>
              </Stack>
              <Typography variant="caption" fontSize={9.5} color="text.disabled">
                {t("dpMonthsValueLabel", { v: signal.effective_months })}
              </Typography>
              {signal.confidence && (
                <Chip label={t("bfConfidenceLabel", { confidence: signal.confidence })} size="small"
                  sx={{ height: 21, fontSize: FONT.CAPTION, bgcolor: C.neutralBg, color: C.neutral }} />
              )}
              <FeatureTag signal={signal} />
            </Stack>
          </Box>
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.4 }}>
            {selected && (
              <CheckCircleIcon sx={{ fontSize: 16, color: C.primary }} />
            )}
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
              sx={{ p: "2px" }}>
              {open
                ? <ExpandLessIcon sx={{ fontSize: 14 }} />
                : <ExpandMoreIcon sx={{ fontSize: 14 }} />
              }
            </IconButton>
          </Box>
        </Stack>

        {/* 상세 펼침 */}
        <Collapse in={open} timeout={150}>
          <Box sx={{ mt: 0.8, pt: 0.8, borderTop: `1px dashed ${C.border}` }}>
            {signal.description && (
              <Typography variant="caption" color="text.secondary" fontSize={11}
                lineHeight={1.6} display="block" sx={{ whiteSpace: "pre-wrap", mb: 0.6 }}>
                {signal.description}
              </Typography>
            )}
            {/* BF feature 적용 가이드 */}
            <Box sx={{ bgcolor: `${C.primary}08`, borderRadius: 1, p: "6px 9px",
              border: `1px solid ${C.primaryL}` }}>
              <Typography variant="caption" fontSize={10} fontWeight={700} color={C.primary}
                display="block" mb={0.3}>
                {t("bfApplyMethodLabel")}
              </Typography>
              <Typography variant="caption" fontSize={10} color="text.secondary" lineHeight={1.6}>
                {signal.direction === "up" || signal.direction === "down"
                  ? t("bfSignalApplyMethodDemand", { sign: dPct > 0 ? "+" : "", pct: dPct.toFixed(0) })
                  : t("bfSignalApplyMethodCategorical")
                }
              </Typography>
              {signal.affected_classes?.length > 0 && (
                <Stack direction="row" spacing={0.4} mt={0.5} flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" fontSize={9} color="text.disabled">{t("bfAffectedItemGroupLabel")}</Typography>
                  {signal.affected_classes.slice(0, 4).map((cls, i) => (
                    <Chip key={i} label={cls} size="small"
                      sx={{ height: 21, fontSize: FONT.CAPTION, bgcolor: C.neutralBg, color: C.neutral }} />
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// ── BF 현황 조회 전용 컴포넌트 ─────────────────────────────────────────────
// AiScenarioPlanning.js 의 현황 조회(Step 0)에서 mod.ExtraInitContent 로 렌더됨

// 제품군별 예측 성능 리스트의 "주의" 비율 열(숫자) 폭 — 실측 동기화(StatusItemList) 전
// 첫 페인트용 폴백값. 실제 폭은 헤더 라벨·전 행 값의 실측 최대값으로 맞춰진다.
const GRP_RIGHT_W = 40;

// 모델별 품목 분포 차트의 높이 하한. 차트는 우측 제품군 리스트 높이를 flex 로 자동
// 추종하므로(같은 CardContent 안 grid + alignItems:stretch) 상수 계산이 필요 없지만,
// 제품군이 적거나 로딩 중이면 차트가 납작해진다. 4행짜리 리스트 높이를 하한으로 잡는다.
// (+20 = StatusItemHeader 높이 — caption 16.5 + mb 0.4)
const BF_MODEL_CHART_MIN_H = statusListH(4) + 20;

// MAPE 수준 색상 — 값이 없으면(미집계) 판정 없음(중립).
// null >= 40 은 false 라 가드가 없으면 조용히 '정상(초록)'으로 떨어진다.
function mapeColor(mape) {
  if (mape == null) return C.neutral;
  if (mape >= 40) return C.danger;
  if (mape >= 25) return C.warning;
  return C.success;
}
// 막대 값 라벨을 차트 최상위 레이어에 그리는 Customized 렌더러 (recharts 2.11).
//   formattedGraphicalItems 에서 막대 계열의 사각형 좌표를 받아 그 위에 숫자를 얹는다.
//   내부 구조에 의존하므로 형태가 다르면 조용히 아무것도 그리지 않는다(차트 자체는 정상).
function renderBarValueLabels(props) {
  const items = props?.formattedGraphicalItems || [];
  const keyOf = (it) => it?.props?.dataKey ?? it?.item?.props?.dataKey;
  // dataKey 로 못 찾으면 "사각형 좌표(width/height)를 가진 계열"을 막대로 간주해 폴백
  const bar = items.find((it) => keyOf(it) === "item_count")
    || items.find((it) => Array.isArray(it?.props?.data) && it.props.data[0]?.width != null);
  const rects = bar?.props?.data;
  if (!Array.isArray(rects)) return null;
  return (
    <g>
      {rects.map((r, i) => (
        r == null || r.x == null || r.y == null ? null : (
          <text key={i} x={r.x + (r.width || 0) / 2} y={r.y - 5} textAnchor="middle"
                style={{ fontSize: FONT.CAPTION, fontWeight: 700, fill: C.accT,
                         stroke: "#fff", strokeWidth: 3, paintOrder: "stroke" }}>
            {r.value}
          </text>
        )
      ))}
    </g>
  );
}

// 방향 배지
function DirBadge({ dir }) {
  if (dir === "up")   return <TrendingUpIcon   sx={{ fontSize: 13, color: C.success }} />;
  if (dir === "down") return <TrendingDownIcon sx={{ fontSize: 13, color: C.danger  }} />;
  return                     <RemoveIcon       sx={{ fontSize: 13, color: C.neutral }} />;
}

// 현행 설정(current_settings)을 base(seasonality) 시나리오 파라미터에 in-place 적용.
// 최신 RELEASED 버전의 plan_config_obj.BF_SETTING(historyHorizon·허용 알고리즘)을 "현행"에 반영.
function applyBfCurrentSettings(cs) {
  if (!cs || cs.source === "none") return;
  const patch = {};
  if (Array.isArray(cs.algorithms) && cs.algorithms.length) patch.algorithms = cs.algorithms.slice();
  if (cs.history_horizon != null && cs.history_horizon !== "") patch.history_horizon = cs.history_horizon;
  if (!Object.keys(patch).length) return;
  const hh = patch.history_horizon;
  const applyToBase = (scns) => (scns || []).forEach(s => {
    if (s.role === "base" && (s.kind || "seasonality") === "seasonality") {
      s.params = { ...s.params, ...patch };
      // 이름/설명도 릴리즈 실제 설정으로 갱신 (하드코딩 "3버킷" 제거)
      if (hh != null) {
        s.name = `현행 (릴리즈 설정 · ${hh}버킷)`;
        s.desc = `최신 릴리즈 버전의 모델 구성 그대로, 평가 버킷 ${hh}.`;
      }
    }
  });
  applyToBase(BF_MODULE.scenarios);
  (BF_MODULE.templates || []).forEach(t => applyToBase(t.scenarios));
}

// ── 현황 드릴다운 → 수요예측 리더보드(제품군 필터) ───────────────────────────
// 워크벤치 등 기존 화면 코드는 건드리지 않고 viewState 주입만으로 처리한다.
// BfLeaderboard(viewName="BF_LEADERBOARD", 메뉴 3208)는 버전전환 reset 이 없어
// selectVersion(id) + filter.category 주입만으로 해당 제품군 기준 조회가 적용된다.
// 단, 리더보드는 itemLevelCd 로 노드의 hrchyLv 를 사용하므로(품목 계층 레벨 필터),
// 품목 계층을 조회해 hrchyCd 매칭 노드(hrchyLv 포함)를 찾아 filter.category 로 넣어야 한다.
function flattenHrchy(node, acc = []) {
  if (!node) return acc;
  acc.push(node);
  (node.children || []).forEach(c => flattenHrchy(c, acc));
  return acc;
}

export async function openBfLeaderboard(dispatch, cond, version) {
  if (!version?.found || version?.id == null) return;   // 릴리즈된 BF 버전 없으면 무시
  const { hrchyCd, hrchyName } = cond || {};
  if (!hrchyCd && !hrchyName) return;                    // 필터 대상 없으면 무시
  const VIEW = "BF_LEADERBOARD";

  // 품목 계층에서 대상 노드(hrchyLv 포함)를 해석 — 리더보드 ItemHrchyTreeFilter 와 동일한 기본 트리
  let node = null;
  try {
    const res = await itemHrchyService.getHrchyFilter({});
    const flat = flattenHrchy(res?.data?.results?.[0]);
    node = (hrchyCd ? flat.find(n => String(n.hrchyCd) === String(hrchyCd)) : null)
        || (hrchyName ? flat.find(n => n.name === hrchyName) : null);
  } catch (e) { /* 노드 미해석 시 버전만으로 이동 */ }

  const payload = { selectVersion: { id: version.id, name: String(version.name ?? "") } };
  if (node) payload.filter = { category: [node] };
  dispatch(reduxUtil.removeViewState({ viewName: VIEW }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(VIEW, payload)));
  dispatch(reduxUtil.updateHistoryState(VIEW));
}

// AI 원인 분석 버전별 캐시 (plan_ver_id → 분석 결과). 같은 버전은 한 번만 호출.
const _bfIssueCache = new Map();


export function BfExtraInitContent({ mod, phase, versionInfo, cachedData, onDataChange, onReadyChange }) {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const goToLeaderboardByGroup = useCallback(
    (grp) => openBfLeaderboard(reduxDispatch, { hrchyCd: grp?.item_group_cd, hrchyName: grp?.item_group }, versionInfo),
    [reduxDispatch, versionInfo],
  );
  // 릴리즈 버전이 있어야 이동 가능 + 대상 화면이 이 테넌트/사용자 권한 안이어야 한다.
  const navOk = versionInfo?.found && versionInfo?.id != null && isPermitted("BF_LEADERBOARD");
  const [data,    setData]    = useState(cachedData ?? null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState(null);
  // AI 원인 분석 (lazy) — 현황 로드 후 비동기 호출
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading,  setAiLoading]  = useState(false);
  // 이슈별 상세(원인·권장) 펼침 상태 — 텍스트가 많아 기본 접고 제목만 스캔, 클릭 시 펼침
  const [openIssues, setOpenIssues] = useState({});

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    scenarioService.getBfInitialState()
      .then(r => setData(r))
      .catch(e => setErr(e?.response?.data?.detail || e.message || t("MSG_IpQueryError")))
      .finally(() => setLoading(false));
  }, [t]);

  // 캐시가 없을 때만 조회
  useEffect(() => {
    if (cachedData) return;
    load();
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // 데이터 변경 시 부모에 캐시 전달
  useEffect(() => {
    if (data) onDataChange?.(data);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // 조회 완료 여부를 부모에 보고 → 상단 액션바(자동 수행 / 시나리오 구성으로) 잠금 해제 기준.
  //   실패(err)도 완료로 본다 — 데이터가 안 와도 사용자가 버튼에 갇히면 안 된다.
  //   AI 원인 분석(aiLoading)은 기준에 넣지 않는다 — 현황 본체와 무관한 부가 정보다.
  useEffect(() => {
    onReadyChange?.(!loading && (!!data || !!err));
  }, [loading, data, err]); // eslint-disable-line react-hooks/exhaustive-deps

  // 최근 실행 기록(빈 상태 패널 우측 요약) — 전 모듈 공용 훅. 미집계일 때만 1건 조회한다.
  //   ⚠️ 훅이라 아래 loading/err early return 보다 앞에 있어야 한다(훅 순서 규칙).
  const lastRun = useLastRunSummary("BF", data?.released_fact_available === false);

  // 현황 로드 후 'AI 원인 분석' 비동기 조회 (현황 GET 을 LLM 으로 막지 않음)
  useEffect(() => {
    if (!data || data?.kpi_current?.avg_mape == null) { setAiAnalysis(null); return; }
    const ver = data.plan_ver_id;
    // 캐시 키에 현재 언어 포함 — 언어 전환 시 옛 언어 결과 재사용 방지 (LLM 출력이 언어별로 다름)
    const key = `${ver == null ? "_latest" : String(ver)}:${i18n.language}`;
    if (_bfIssueCache.has(key)) {            // 같은 버전·언어 → 재호출 없이 캐시 즉시 표시
      setAiAnalysis(_bfIssueCache.get(key));
      setAiLoading(false);
      return;
    }
    let alive = true;
    setAiLoading(true);
    scenarioService.getBfIssueAnalysis(ver)
      .then(r => { _bfIssueCache.set(key, r); if (alive) setAiAnalysis(r); })
      .catch(() => { if (alive) setAiAnalysis(null); })
      .finally(() => { if (alive) setAiLoading(false); });
    return () => { alive = false; };
  }, [data?.plan_ver_id, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 현행(base) 시나리오를 최신 RELEASED 버전 설정으로 채움 + 계절성 조합 주입 ──
  useEffect(() => {
    if (!data) return;
    applyBfCurrentSettings(data.current_settings);
    BF_MODULE._currentSettings = data.current_settings || null;   // DB 템플릿 base 현행값 적용용(fetchTemplates에서 사용)
    BF_MODULE._seasonalCombos = data.seasonal_combos || [];
    BF_MODULE._scenarioVersionName = data.scenario_version_name || null;
    BF_MODULE._approvedSignals = data.approved_signals || [];
  }, [data]);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: "16px !important" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} sx={{ color: C.primary }} />
            <Typography variant="caption" color="text.secondary">
              {t("bfStatusAnalyzingDesc")}
            </Typography>
          </Stack>
          <LinearProgress sx={{ mt: 1.5, borderRadius: 2, height: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${C.danger}30` }}>
        <CardContent sx={{ p: "10px 14px !important" }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <WarningAmberIcon sx={{ fontSize: 15, color: C.warning }} />
            <Typography variant="caption" color={C.warning}>
              {t("MSG_IpStatusQueryFailed", { err })}
            </Typography>
            <IconButton size="small" onClick={load} sx={{ ml: "auto !important", p: "3px" }}>
              <RefreshIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // 미집계 = 리더보드 집계 결과 없음. 응답 공통 필드 하나로 판정한다(전 모듈 동일 기준).
  //   ⚠️ 이 값 하나로 화면의 모든 데이터 소스를 일괄 게이트한다 — 일부 섹션만 값이 남으면
  //   "어디는 보이고 어디는 —"인 반쪽 화면이 되어 판정 가능 여부를 오독하게 만든다.
  const bfNotAggregated = data?.released_fact_available === false;

  // 표준 initial-state 골격(flat): kpi_current/model_distribution 최상위
  const kpi       = bfNotAggregated ? {} : (data?.kpi_current || {});
  const modelDist = bfNotAggregated ? {} : (data?.model_distribution || {});
  const groups    = bfNotAggregated ? [] : (data?.item_group_analysis || []);
  const signals   = bfNotAggregated ? [] : (data?.approved_signals || []);
  // 버전 표시: versionInfo(latestVersions) > data.plan_ver_name > plan_ver_id 순 우선

  // ── 2열 배치 여부 + 제품군 막대 상대 스케일 기준 ──────────────────────────────
  const hasDist  = Object.keys(modelDist).length > 0;
  const hasGroups = groups.length > 0;
  const twoCol   = hasDist && hasGroups;
  // 모델별 품목 분포 차트 데이터 — 품목 수 내림차순. top = 최다 모델(막대 강조용)
  const modelChartData = Object.entries(modelDist)
    .map(([model, info]) => {
      // 오차 선은 실현 WAPE(realized_wape) 우선 — 리더보드(선정 백테스트)는 과거 여러 버킷
      // 평균이라 실현보다 낮게 나와 헤드라인과 괴리를 만든다. 실현이 없으면(선정 폴백 상태)
      // 헤드라인도 선정 기준이므로 avg_wape 로 폴백해 기준을 항상 헤드라인과 일치시킨다.
      const w = info?.realized_wape ?? info?.avg_wape;
      return {
        model,
        item_count: Number(info?.item_count) || 0,
        avg_wape: w == null ? null : Number(w),
      };
    })
    .sort((a, b) => b.item_count - a.item_count)
    .map((d, i) => ({ ...d, top: i === 0 }));

  // pv=1 월별 실현 오차 추세 — 헤드라인(실현 오차)과 동일 소스(pv=1)·규칙. 릴리즈가 쌓일수록
  // 과거 월이 누적되어 점이 늘어난다. 소표본 헤드라인이 튀는지 판단할 맥락을 제공한다.
  const realizedTrend  = bfNotAggregated ? { points: [] } : (data?.realized_mape_trend || { points: [] });
  const trendChartData = (Array.isArray(realizedTrend.points) ? realizedTrend.points : [])
    .filter(p => p && p.month)
    .map(p => ({ month: p.month, mape: p.mape == null ? null : Number(p.mape), combos: Number(p.combos) || 0 }));
  const hasTrend = trendChartData.length > 0;

  // 최근 월 예측오차 카드 — 값 = 최근 월 WAPE(미완결 당월 제외), 배지 = 직전 3개월 평균 대비
  // 델타(%p), 서브라인 = 직전 3개월 평균. 최근 월 1개만 있으면 값만 표시(배지·서브라인 생략).
  const trendPts    = trendChartData.filter(p => p.mape != null);
  const trendLast   = trendPts.length ? trendPts[trendPts.length - 1] : null;
  const trendPrev   = trendPts.slice(-4, -1);
  const trendPrevAvg = (trendLast && trendPrev.length)
    ? +(trendPrev.reduce((s, p) => s + p.mape, 0) / trendPrev.length).toFixed(1)
    : null;
  const trendDelta  = trendPrevAvg != null ? +(trendLast.mape - trendPrevAvg).toFixed(1) : null;

  // 수요패턴별 정확도(WAPE) — 희소 조합을 필터링하는 대신 패턴(안정/변동/간헐/불규칙)으로 분해.
  // 패턴별 WAPE·판매량 비중을 나란히 보여, 오차 기여와 물량 기여를 사용자가 직접 판단하게 한다
  // (비중이 크다/작다는 테넌트 데이터에 따라 다르므로 문구로 단정하지 않는다).
  const patternData = (bfNotAggregated ? [] : (data?.realized_wape_by_pattern?.classes || []))
    .map(c => ({
      key:      c.key,
      label:    c.label,
      combos:   Number(c.combos) || 0,
      wape:     c.wape == null ? null : Number(c.wape),
      volShare: Number(c.vol_share) || 0,
    }));
  const hasPattern = patternData.length > 0;


  // 대표 오차율 카드 라벨 — 기준(mape_basis)에 따라 의미가 다르므로 라벨로 구분한다.
  //  realized  : 이 릴리즈 예측이 실제로 맞았나(도래 실적 대비). 표본은 도래분에 한정 →
  //              도래 건수를 라벨에 노출해 소표본임을 드러낸다.
  //  selection : 모델 선정 시점 백테스트 오차(YOY 등 미측정 제외한 measured_items 기준).
  const bfMapeIsRealized = kpi.mape_basis === "realized";
  const bfMapeLabel = bfMapeIsRealized
    ? t("bfRealizedMapeLabel", { c: (kpi.realized_combo_count ?? 0).toLocaleString() })
    : t("bfSelectionMapeLabel");

  // 현재 문제점 섹션의 'AI 원인 분석' 카드 심각도 색상 (danger/warning/info)
  const SEV = {
    danger:  { c: C.danger,  bg: C.dangerBg },
    warning: { c: C.warning, bg: C.warningBg },
    info:    { c: C.primary, bg: C.primaryBg },
  };

  return (
    <Stack spacing={1.5}>

      {/* 미집계 빈 상태 패널 — 화면에서 유일한 solid CTA(계획 프로세스 관리로 이동) */}
      {bfNotAggregated && (() => {
        const verName   = data?.plan_ver_name ?? versionInfo?.name ?? null;
        const releaseTs = data?.plan_release_ts ?? versionInfo?.releaseTs ?? null;
        return (
          <StatusEmptyPanel
            title={t("statusNoAggregatedResultTitle")}
            desc={verName
              ? t("statusNoAggregatedResultDesc", { name: verName })
              : t("statusNoReleasedVersionDesc")}
            ctaLabel={t("menuDpProcessMgmt")}
            onCta={() => openPlanRunScreen(reduxDispatch, "DP_PROCESS_MGMT")}
            summary={buildStatusPanelSummary(t, {
              verName,
              releaseTs: releaseTs ? new Date(releaseTs).toLocaleDateString() : null,
              lastRun,
            })}
          />
        );
      })()}

      {/* ── 섹션 1: BF 현재 성능 요약 ── */}
      <StatusKpiSection
        collapsible
        defaultOpen
        versionInfo={versionInfo}
        right={bfNotAggregated ? <StatusPendingTag /> : undefined}
        cards={kpi.avg_mape != null ? [
                // ⚠️ 헤더 칩(최신 릴리즈)이 모든 카드를 그 릴리즈 소속으로 보이게 하므로,
                // 카드마다 서브라인으로 '자기 기준'을 명시한다:
                //  - 오차·추세: 과거 릴리즈들이 서비스한 예측 vs 도래 실적 (릴리즈 소속 아님)
                //  - BF 대상·주요 모델: 최신 릴리즈(칩 버전)의 리더보드 기준
                { label: bfMapeLabel, value: `${kpi.avg_mape}%`,
                  color: mapeColor(kpi.avg_mape),
                  footer2: bfMapeIsRealized
                    ? t("bfScopeRealizedHint", { p: kpi.realized_months || "" })
                    : t("bfScopeSelectionHint") },
                // BF 대상 품목 = 측정된(비YOY) 품목 수. YOY(bf_flg='N')는 예측이 아닌 폴백이라 제외.
                { label: t("bfTargetItemCountLabel"),
                  value: kpi.measured_items != null ? kpi.measured_items.toLocaleString() + t("bfCountUnit") : null,
                  color: C.primary,
                  footer2: data?.plan_ver_name ? t("bfScopeReleaseHint", { v: data.plan_ver_name }) : "" },
                { label: t("bfBestModelLabel"),
                  value: kpi.best_model_pct != null ? `${kpi.best_model || "-"} (${kpi.best_model_pct}%)` : null,
                  color: C.teal,
                  footer2: data?.plan_ver_name ? t("bfScopeReleaseHint", { v: data.plan_ver_name }) : "" },
                // 최근 월 예측오차 — 값 = 최근 월 WAPE(헤드라인의 기간 전체 오차와 바로 비교됨),
                // 배지(valueRight) = 직전 3개월 평균 대비 델타. 오차 감소=개선=초록.
                { label: t("bfRecentMonthErrorLabel"),
                  value: trendLast ? `${+trendLast.mape.toFixed(1)}%` : null,
                  color: trendLast ? mapeColor(trendLast.mape) : C.neutral,
                  valueRight: trendDelta == null ? null : (
                    <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, whiteSpace: "nowrap",
                      color: Math.abs(trendDelta) < 0.05 ? C.neutral
                           : trendDelta > 0 ? C.danger : C.success }}>
                      {Math.abs(trendDelta) < 0.05 ? t("bfTrendFlat")
                        : trendDelta > 0 ? t("bfTrendWorsened", { d: trendDelta })
                        : t("bfTrendImproved", { d: Math.abs(trendDelta) })}
                    </Typography>
                  ),
                  footer2: trendPrevAvg != null ? t("bfTrendPrevAvgHint", { avg: trendPrevAvg }) : "" },
              ].map((kp, i) => ({
                key: i, label: kp.label, value: kp.value,
                color: kp.color, footer2: kp.footer2 ?? "", valueRight: kp.valueRight,
              })) : []}
        empty={kpi.avg_mape != null ? null : <StatusEmpty text={t("bfNoCompletedVersionDesc")} />}
      />

      {/* ── 섹션: 실적 대비 오차 상세 — 월별 추이 · 수요 패턴을 좌우 2단으로 (예측 성능 분석과 동일 레이아웃)
           (구간 분포 스택 바 섹션은 제거 — 품목수/판매량 이중 축이 오해를 불러, 같은 정보를
            수요 패턴별 정확도 표가 패턴이라는 의미 있는 축으로 대신한다.) ── */}
      {!bfNotAggregated && (
        <Box sx={{ display: "grid", gap: 1.5, alignItems: "start",
          gridTemplateColumns: { xs: "1fr", md: hasPattern ? "1fr 1fr" : "1fr" } }}>
        <StatusSection
          collapsible
          defaultOpen
          collapseDisabled={!hasTrend}
          title={t("bfRealizedMapeTrendTitle")}
          right={!hasTrend ? <StatusPendingTag /> : undefined}
          contentSx={{ p: "14px 15px !important" }}
        >
          <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, mb: 1 }}>
            {t("bfRealizedMapeTrendHint")}
          </Typography>
          {!hasTrend
            ? <StatusEmpty text={t("bfRealizedMapeTrendEmpty")} />
            : (
              <Box sx={{ width: "100%", height: BF_MODEL_CHART_MIN_H }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trendChartData} margin={{ top: 14, right: 12, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: FONT.CAPTION, fill: C.text2 }} tickMargin={6}
                           interval="preserveStartEnd" axisLine={{ stroke: C.border }} tickLine={false} />
                    <YAxis unit="%" domain={[0, 100]} width={40}
                           tick={{ fontSize: FONT.CAPTION, fill: C.text2 }} axisLine={false} tickLine={false} />
                    <RTooltip cursor={{ stroke: C.border }} content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const pt = payload[0].payload;
                      return (
                        <Box sx={{ bgcolor: C.bg2, border: `1px solid ${C.border}`, borderRadius: 1, p: "6px 8px" }}>
                          <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1 }}>{label}</Typography>
                          <Typography sx={{ fontSize: FONT.CAPTION, color: C.g2 }}>
                            WAPE: {pt.mape == null ? "-" : `${pt.mape}%`}
                          </Typography>
                          <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                            {t("bfRealizedComboLabel", { c: (pt.combos || 0).toLocaleString() })}
                          </Typography>
                        </Box>
                      );
                    }} />
                    <Line dataKey="mape" name="WAPE"
                          stroke={C.g2} strokeWidth={2} dot={{ r: 3, fill: C.g2, strokeWidth: 0 }}
                          connectNulls isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            )}
        </StatusSection>
        {/* 우: 수요 패턴별 정확도 (WAPE) — 희소 조합을 패턴(안정/변동/간헐/불규칙)으로 분해 */}
        {hasPattern && (
        <StatusSection
          collapsible
          defaultOpen
          title={t("bfDemandPatternTitle")}
          contentSx={{ p: "14px 15px !important" }}
        >
          <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, mb: 1.2 }}>
            {t("bfDemandPatternHint")}
          </Typography>
          {/* 좌우 2단 카드 안이므로 WAPE 바 컬럼을 1fr 로 늘려 카드 폭을 꽉 채운다 */}
          <Box sx={{ display: "grid", gridTemplateColumns: "auto auto auto minmax(120px,1fr)",
            gap: "9px 20px", alignItems: "center" }}>
            {[t("bfPatternColLabel"), t("bfColCombos"), t("bfColVolShare"), "WAPE"].map((h, i) => (
              <Typography key={`ph${i}`} sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2,
                textAlign: (i === 1 || i === 2) ? "right" : "left" }}>{h}</Typography>
            ))}
            {patternData.map(p => (
              <React.Fragment key={p.key}>
                <Box sx={{ minWidth: 0 }}>
                  {/* 패턴명 앞 심각도 색 점 — 개별 품목이 아니라 '분류(카테고리)'임을 시각적으로 구분 */}
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                      bgcolor: mapeColor(p.wape ?? 0) }} />
                    <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1, lineHeight: 1.25 }}>
                      {t(`bfPattern_${p.key}`, { defaultValue: p.label })}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, lineHeight: 1.25, pl: "14px" }}>
                    {t(`bfPatternDesc_${p.key}`, { defaultValue: "" })}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: FONT.BODY, color: C.text2, textAlign: "right" }}>
                  {p.combos.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: FONT.BODY, color: C.text2, textAlign: "right" }}>
                  {p.volShare}%
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
                  <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: C.border, overflow: "hidden", minWidth: 40 }}>
                    <Box sx={{ width: `${Math.min(p.wape ?? 0, 100)}%`, height: "100%",
                      bgcolor: mapeColor(p.wape ?? 0), borderRadius: 4 }} />
                  </Box>
                  <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: mapeColor(p.wape ?? 0),
                    minWidth: 42, textAlign: "right" }}>
                    {p.wape == null ? "-" : `${p.wape}%`}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </StatusSection>
        )}
        </Box>
      )}

      {/* ── 섹션: 모델별 품목 분포 + 제품군별 예측 성능 (2열) ──
           데이터가 없어도 섹션은 자리를 지킨다 — 사라지면 아래 섹션들이 위로 튄다. */}
      <StatusSection
        collapsible
        defaultOpen
        collapseDisabled={!hasDist && !hasGroups}
        title={t("bfForecastPerfAnalysisTitle")}
        right={(!hasDist && !hasGroups) ? <StatusPendingTag /> : undefined}
        contentSx={{ p: "14px 15px !important" }}
      >
        {(!hasDist && !hasGroups) && <StatusEmpty variant="chart" />}
        <Box sx={{ display: "grid", gap: 2, alignItems: "stretch",
          gridTemplateColumns: { xs: "1fr", md: twoCol ? "1fr 1fr" : "1fr" } }}>
          {hasDist && (
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
              <Stack direction="row" alignItems="center" spacing={0.8} mb={1}>
                <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>
                  {t("bfModelItemDistributionTitle")}
                </Typography>
                <Chip label={t("bfModelCountLabel", { n: Object.keys(modelDist).length })} size="small"
                  sx={{ height: 20, fontSize: FONT.CAPTION, bgcolor: C.tealBg, color: C.teal }} />
              </Stack>
                {/* 막대=품목 수(좌축, 자동 스케일) / 선=평균 MAPE(우축, 0~100% 고정).
                    MAPE 축을 고정해야 모델 간·릴리즈 간 선 높이를 그대로 비교할 수 있다.
                    막대색은 단색 — 모델별 10색은 난립이라 최다 모델만 진한 accent 로 강조한다. */}
                {/* 차트 높이는 고정 px 가 아니라 flex 로 채운다 — 좌우 컬럼이 같은 grid
                    (alignItems:stretch) 라 row 높이는 큰 쪽(대개 우측 제품군 리스트)에
                    맞춰지는데, 고정 px 로 두면 차트 아래에 그만큼 빈 공간이 남는다.
                    행 수 기반 상수 추정은 제목 줄바꿈·언어·행 내부 칩 높이를 못 따라가
                    어긋나므로 쓰지 않는다. */}
                <Box sx={{ flex: 1, minWidth: 0, minHeight: BF_MODEL_CHART_MIN_H }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={modelChartData} barCategoryGap="20%"
                                   margin={{ top: 18, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                      <XAxis dataKey="model" tick={{ fontSize: FONT.CAPTION, fill: C.text2 }} tickMargin={6}
                             interval={0} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis yAxisId="left" tick={{ fontSize: FONT.CAPTION, fill: C.text2 }} width={44}
                             axisLine={false} tickLine={false} />
                      <YAxis yAxisId="right" orientation="right" unit="%" domain={[0, 100]} width={40}
                             tick={{ fontSize: FONT.CAPTION, fill: C.text2 }} axisLine={false} tickLine={false} />
                      <RTooltip content={<AgStyleTooltipContent />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                      {/* payload 를 명시해 레전드 순서를 고정 — 렌더 순서(선 먼저)와 무관하게 품목 수가 앞 */}
                      <Legend wrapperStyle={{ fontSize: FONT.BODY, paddingTop: 4 }} iconType="circle" iconSize={9}
                              payload={[
                                { value: t("itemCount"), type: "circle", color: C.accM },
                                { value: t("axisForecastErrorWape"), type: "circle", color: C.g2 },
                              ]} />
                      {/* fill 은 레전드 아이콘 색 — Cell 로만 칠하면 레전드가 검정으로 남는다.
                          isAnimationActive={false} — 탭 왕복 시 리마운트마다 그리기
                          애니메이션이 재생되는 것을 막는다(현황 탭 차트 공통). */}
                      <Bar yAxisId="left" dataKey="item_count" name={t("itemCount")} maxBarSize={34}
                           fill={C.accM} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                        {modelChartData.map((d) => (
                          <Cell key={d.model} fill={d.top ? C.acc : C.accM} />
                        ))}
                      </Bar>
                      <Line yAxisId="right" dataKey="avg_wape" name={t("axisForecastErrorWape")}
                            stroke={C.g2} strokeWidth={2} dot={{ r: 3, fill: C.g2, strokeWidth: 0 }}
                            connectNulls isAnimationActive={false} />
                      {/* 막대 값 라벨은 최상위 레이어에 따로 그린다 — recharts 는 선언 순서대로 SVG 에 그려서
                          Bar 의 LabelList 로 두면 뒤에 오는 Line 이 숫자를 덮고, 반대로 Line 을 앞에 두면
                          높은 막대가 선을 가린다. Customized 를 마지막에 두면 둘 다 피한다. */}
                      <Customized component={renderBarValueLabels} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
            </Box>
          )}
          {hasGroups && (
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
              <Stack direction="row" alignItems="center" spacing={0.8} mb={1} flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>
                  {t("bfCategoryForecastPerfTitle")}
                </Typography>
                <Chip label={t("bfCategoryCountLabel", { n: groups.length })} size="small"
                  sx={{ height: 20, fontSize: FONT.CAPTION, bgcolor: C.purpleBg, color: C.purple }} />
                <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                  {t("bfBarLegendNote")}
                </Typography>
              </Stack>
            {/* 막대는 절대 0~100% 스케일 — 좌측 모델 분포 차트의 MAPE 우축(domain [0,100])과 스케일을 맞춘다.
                (막대 짧을수록 정확. 30% 세로선은 주의 기준, MAPE 100% 초과는 가득 참으로 클램프) */}
            <StatusItemList>
            {/* 헤더의 눈금 라벨이 각 행 바의 30% 기준선을 설명한다 — 선만 그으면 무슨 선인지 알 수 없다 */}
            <StatusItemHeader
              nameLabel={`${t("itemGroupLabel")} / ${t("itemCount")}`}
              rightW={0}
              barScale={{ marks: [{ at: 50, label: t("bfMapeWarnMarkLabel"),
                                    title: t("bfMapeWarnMarkHint") }] }}
              barValue={{ w: 78, label: t("bfForecastErrorLabel"), unit: "%" }}
              /* 주의 품목 비율 — pill 뱃지였으나 안에 숫자가 들어 있어 폭이 행마다 달랐다.
                 라벨을 헤더로 올리고 값은 숫자/단위 2열로 쪼갠다(정렬 규격). */
              cells={[{ w: GRP_RIGHT_W, label: t("warning"), unit: "%" }]} />
            <Stack spacing={0.7} sx={{ flex: 1, justifyContent: "flex-start" }}>
              {groups.map((grp, idx) => {
                const mc         = mapeColor(grp.avg_wape);
                const groupNav   = navOk && (grp.item_group_cd || grp.item_group);
                // 주의 열 = 판매량 비중(그룹 판매량 중 WAPE>50% 조합 비중). 그룹 WAPE(물량가중)와
                // 같은 축이라 "WAPE 45%인데 주의 90%" 개수 괴리가 사라진다.
                const hp   = Number(grp.high_vol_pct) || 0;
                const hClr = hp >= 30 ? C.danger : hp >= 10 ? C.warning : C.neutral;
                const cntText = t("bfCategoryItemCountLabel", { n: grp.item_count });
                return (
                  <StatusItemRow key={idx}
                    accent={mc}
                    name={<NavName targets={[
                      { label: i18n.t("menuBfLeaderboard"),
                        onClick: groupNav ? () => goToLeaderboardByGroup(grp) : undefined },
                    ]}>{grp.item_group}</NavName>}
                    sub={cntText}
                    /* MAPE 게이지 — 절대 스케일(StatusItemBar 기본 max 100, 초과분은 내부 클램프).
                       릴리즈·제품군을 넘나들며 막대 길이를 그대로 비교할 수 있어야 하므로 상대 스케일을 쓰지 않는다.
                       하한을 깔지 않는다 — 작은 값은 StatusItemBar 의 minWidth 3px 이 이미 보장한다.
                       marker=50 은 우측 '주의' 열의 WAPE>50% 임계와 같은 기준선. */
                    bar={{ value: Number(grp.avg_wape) || 0, color: mc,
                           marker: 50, markerLabel: t("bfMapeWarnMarkHint") }}
                    barValue={{ w: 78, unit: grp.avg_wape != null ? "%" : "",
                      num: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: mc, lineHeight: 1 }}>
                          {grp.avg_wape ?? "—"}
                        </Typography>
                      ) }}
                    /* 주의 판매량 비중 — WAPE>50% 조합의 판매량 비중(실현 데이터 없으면 개수 비율 폴백).
                       값 자체는 숫자이므로 pill 대신 일반 지표 열로 둔다(라벨은 헤더가 갖는다). */
                    cells={[
                      { w: GRP_RIGHT_W, unit: "%", num: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: hClr, lineHeight: 1,
                                          whiteSpace: "nowrap" }}>
                          {hp.toFixed(0)}
                        </Typography>
                      ) },
                    ]} />
                );
              })}
            </Stack>
            </StatusItemList>
            </Box>
          )}
        </Box>
      </StatusSection>

      {/* ── 섹션: 현재 문제점 · 개선 포인트 (현황 데이터에서 도출) ── */}
      {kpi.avg_mape != null && (
        <StatusSection
          collapsible
          defaultOpen
          title={t("bfCurrentIssuesTitle")}
          right={
            aiLoading
              ? <Chip label={t("bfAnalyzingLabel")} size="small"
                  sx={{ height: 20, fontSize: FONT.CAPTION, fontWeight: 700, bgcolor: C.purpleBg, color: C.purple }} />
              : aiAnalysis?.issues?.length
                ? <Chip label={t("countCases", { count: aiAnalysis.issues.length })} size="small"
                    sx={{ height: 20, fontSize: FONT.CAPTION, fontWeight: 700,
                          bgcolor: C.warningBg, color: C.warning }} />
                // "양호"(초록) 배지를 쓰지 않는다 — 이슈 0건은 판정 결과가 아니라 집계 대기일 수 있다.
                : <StatusPendingTag />
          }
          contentSx={{ p: "12px 14px !important" }}
        >
          {/* ── AI 원인 분석 (비동기 로드) ── */}
          <Box>
            <Stack direction="row" spacing={0.7} alignItems="center" mb={0.8}>
              <AutoAwesomeIcon sx={{ fontSize: 15, color: C.purple }} />
              <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.purple }}>
                {t("bfAiCauseAnalysisTitle")}
              </Typography>
              {aiAnalysis?.source === "rule" && (
                <Chip label={t("overview")} size="small"
                  sx={{ height: 18, fontSize: FONT.MICRO, bgcolor: C.neutralBg, color: C.neutral }} />
              )}
            </Stack>

            {aiLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                <CircularProgress size={14} sx={{ color: C.purple }} />
                <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>
                  {t("bfAiAnalyzingDesc")}
                </Typography>
              </Stack>
            ) : aiAnalysis && (aiAnalysis.issues?.length || fieldText(t, aiAnalysis, "summary")) ? (
              <Stack spacing={0.8}>
                {fieldText(t, aiAnalysis, "summary") && (
                  <Typography sx={{ fontSize: FONT.BODY, color: C.text2, lineHeight: 1.55,
                    bgcolor: C.purpleBg, borderRadius: 1.5, p: "8px 10px" }}>
                    {fieldText(t, aiAnalysis, "summary")}
                  </Typography>
                )}
                {(aiAnalysis.issues || []).map((it, i) => {
                  const s = SEV[it.severity] || SEV.info;
                  // 항목 서술도 병행 키(title_key·cause_key·recommendation_key) 우선.
                  const itTitle = fieldText(t, it, "title");
                  const itCause = fieldText(t, it, "cause");
                  const itRec   = fieldText(t, it, "recommendation");
                  const hasDetail = itCause || itRec;
                  const open = openIssues[i] ?? (i === 0);   // 가장 시급한 첫 이슈만 기본 펼침
                  return (
                    <Box key={i} sx={{ display: "flex", gap: 1, p: "8px 10px", borderRadius: 1.5,
                      bgcolor: C.bg2, border: `1px solid ${C.border}` }}>
                      <Box sx={{ width: 3, borderRadius: 2, bgcolor: s.c, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        {/* 제목 행 — 클릭 시 원인·권장 펼침/접힘 (텍스트 벽 방지) */}
                        <Stack direction="row" alignItems="center" spacing={0.6} flexWrap="wrap" useFlexGap
                          onClick={hasDetail ? () => setOpenIssues(p => ({ ...p, [i]: !open })) : undefined}
                          sx={{ cursor: hasDetail ? "pointer" : "default" }}>
                          <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: s.c, lineHeight: 1.3 }}>
                            {itTitle}
                          </Typography>
                          {it.evidence && (
                            <Chip label={it.evidence} size="small"
                              sx={{ height: 18, fontSize: FONT.MICRO, bgcolor: s.bg, color: s.c }} />
                          )}
                          {hasDetail && (
                            <Box sx={{ ml: "auto !important", display: "flex", alignItems: "center" }}>
                              {open ? <ExpandLessIcon sx={{ fontSize: 16, color: C.text3 }} />
                                    : <ExpandMoreIcon sx={{ fontSize: 16, color: C.text3 }} />}
                            </Box>
                          )}
                        </Stack>
                        <Collapse in={open} unmountOnExit>
                          {itCause && (
                            <Typography sx={{ fontSize: FONT.BODY, color: C.text2, lineHeight: 1.5, mt: 0.3 }}>
                              <b style={{ color: C.text1 }}>{t("bfCauseLabel")}</b> · {itCause}
                            </Typography>
                          )}
                          {itRec && (
                            <Typography sx={{ fontSize: FONT.BODY, color: C.primary, lineHeight: 1.5, mt: 0.2 }}>
                              <b>{t("bfRecommendationLabel")}</b> · {itRec}
                            </Typography>
                          )}
                        </Collapse>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>
                {/* available:false 는 summary_key(또는 error_code)로 실패 이유를 싣는다 */}
                {tKeyOr(t, aiAnalysis?.summary_key, "")
                  || (aiAnalysis?.error_code ? t("aiNarrativeUnavailable") : "")
                  || t("bfAiAnalysisFailedDesc")}
              </Typography>
            )}
          </Box>
        </StatusSection>
      )}

      {/* ── 섹션 3: 마케팅 인텔리전스 요약 ── */}
      <StatusSection
        collapsible
        defaultOpen
        icon={<CampaignIcon sx={{ fontSize: 15, color: C.primary }} />}
        title={t("bfMarketingIntelTitle")}
        right={
          <Chip
            label={t("bfApprovedSignalCountLabel", { count: signals.length })}
            size="small"
            sx={{
              height: 20, fontSize: FONT.CAPTION, fontWeight: 700,
              bgcolor: signals.length > 0 ? C.primaryBg : C.neutralBg,
              color:   signals.length > 0 ? C.primary   : C.neutral,
            }}
          />
        }
        contentSx={{ p: "12px 14px !important" }}
      >

          {signals.length === 0 ? (
            // 빈 상태 표현은 전 모듈 공용 컴포넌트로 통일(점선 보더 + info 아이콘 + 회색 문구).
            <StatusEmpty text={t("bfNoApprovedSignalDesc")} />
          ) : (
            <Stack spacing={0.8}>
              {/* 방향별 집계 */}
              <Stack direction="row" spacing={1} mb={0.6} flexWrap="wrap" useFlexGap>
                {[
                  { dir: "up",        label: "bfDemandUpLabel", color: C.success,   bg: C.successBg },
                  { dir: "down",      label: "bfDemandDownLabel", color: C.danger,    bg: C.dangerBg  },
                  { dir: "uncertain", label: "bfUncertainLabel", color: C.neutral,  bg: C.neutralBg },
                ].map(d => {
                  const cnt = signals.filter(s => s.direction === d.dir).length;
                  if (cnt === 0) return null;
                  return (
                    <Chip key={d.dir}
                      icon={<DirBadge dir={d.dir} />}
                      label={`${t(d.label)} ${t("countCases", { count: cnt })}`}
                      size="small"
                      sx={{ height: 22, fontSize: FONT.BODY, fontWeight: 700,
                        bgcolor: d.bg, color: d.color,
                        "& .MuiChip-icon": { ml: "4px" } }}
                    />
                  );
                }).filter(Boolean)}
                {/* 총 영향 기간 */}
                <Chip label={t("bfAvgImpactMonthsLabel", { n: Math.round(signals.reduce((s,x) => s + x.effective_months, 0) / Math.max(signals.length, 1)) })}
                  size="small"
                  sx={{ height: 22, fontSize: FONT.BODY, bgcolor: C.tealBg, color: C.teal }} />
              </Stack>

              {/* 신호 목록 (접기 가능, 상위 3개 기본 표시) */}
              {signals.slice(0, 3).map((sig, i) => {
                const dPct = Number(sig.demand_impact_pct || 0);
                const dc = dPct > 0 ? C.success : dPct < 0 ? C.danger : C.neutral;
                return (
                  <Box key={sig.id || i} sx={{
                    display: "flex", gap: 0.8, alignItems: "flex-start",
                    p: "6px 8px", bgcolor: `${dc}06`, borderRadius: 1,
                    border: `1px solid ${dc}20`,
                  }}>
                    <DirBadge dir={sig.direction} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontSize={FONT.SUB} fontWeight={600}
                        lineHeight={1.3} sx={{
                          overflow: "hidden", textOverflow: "ellipsis",
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                        }}>
                        {sig.title}
                      </Typography>
                      <Stack direction="row" spacing={0.5} mt={0.3} flexWrap="wrap" useFlexGap>
                        <Chip label={t("bfDemandPctLabel", { sign: dPct > 0 ? "+" : "", pct: dPct.toFixed(0) })} size="small"
                          sx={{ height: 21, fontSize: FONT.CAPTION, fontWeight: 700,
                            bgcolor: `${dc}15`, color: dc }} />
                        <Chip label={t("dpMonthsValueLabel", { v: sig.effective_months })} size="small"
                          sx={{ height: 21, fontSize: FONT.CAPTION, bgcolor: C.neutralBg, color: C.neutral }} />
                        {sig.confidence && (
                          <Chip label={t("bfConfidenceLabel", { confidence: sig.confidence })} size="small"
                            sx={{ height: 21, fontSize: FONT.CAPTION, bgcolor: C.neutralBg, color: C.neutral }} />
                        )}
                      </Stack>
                    </Box>
                    {/* BF feature 연계 힌트 */}
                    <Box sx={{ flexShrink: 0 }}>
                      <Chip
                        label={t("bfLinkableLabel")}
                        size="small"
                        sx={{ height: 21, fontSize: FONT.CAPTION, fontWeight: 700,
                          bgcolor: C.primaryBg, color: C.primary,
                          border: `1px solid ${C.primaryL}` }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
      </StatusSection>
    </Stack>
  );
}

// BF_MODULE에 ExtraInitContent 등록 (선언 후에 할당)
BF_MODULE.ExtraInitContent = BfExtraInitContent;

// ── 표준 시나리오 템플릿 (시나리오 구성 탭 — 표준 시나리오 모드). 공통 화면이 mod.templates 로 참조 ──
// SCN-BF-001 / SCN-BF-002 를 표준 시나리오로 제공. 선택 시 기준 시나리오/시나리오 1/시나리오 2 파라미터가 3열 편집기에 로드된다.
// UI 구성 단계 — 실제 엔진(forecast_compare 시뮬 / plan_config_obj 확정) 반영은 추후 연결.
// templates[].name/industry/useCase/desc 값은 i18n 키 문자열 — 공통 화면(AiScenarioPlanning.js)이
//   t(tpl.name) 등으로 변환한다. tags/scenarios[].name·desc 도 i18n 키 문자열 — 공통 화면의
//   makeAddedScenario/makeBaseScenario(desc/tags는 tr(), base name은 t())가 화면 값으로 변환한다.
BF_MODULE.templates = [
  {
    id: "scn_bf_001",
    industry: "dpTplTagSeasonality",
    name: "bfTpl001Name",
    useCase: "bfTpl001UseCase",
    desc: "bfTpl001Desc",
    tags: ["scnTagSeasonality", "scnTagAlgoLimited"],
    scenarios: [
      { role: "base", name: "scnBfTplSeasonalityBaseName", kind: "seasonality", desc: "scnBfTplSeasonalityBaseDesc",
        params: { target_group: "all", algorithms: ["AR", "HW", "PR", "XGB", "RF"], history_horizon: 3 } },
      { role: "alt1", name: "scnBfTplSeasonalityAlt1Name", kind: "seasonality", desc: "scnBfTplSeasonalityAlt1Desc",
        params: { target_group: "seasonal", algorithms: ["AR", "HW", "PR"], history_horizon: 6 } },
      { role: "alt2", name: "scnBfTplSeasonalityAlt2Name", kind: "seasonality", desc: "scnBfTplSeasonalityAlt2Desc",
        params: { target_group: "seasonal", algorithms: ["AR", "HW", "PR"], history_horizon: 12 } },
    ],
  },
  {
    id: "scn_bf_002",
    industry: "bfTplIndustryExternalFactor",
    name: "bfTpl002Name",
    useCase: "bfTpl002UseCase",
    desc: "bfTpl002Desc",
    tags: ["scnTagExternalFactor", "scnTagOilRawMaterial"],
    scenarios: [
      { role: "base", name: "scnBfTplExternalFactorBaseName", kind: "external_factor", desc: "scnBfTplExternalFactorBaseDesc",
        params: { external_factors: [], algorithms: ["AR", "HW", "PR", "XGB", "RF"], influence_threshold: 0.7 } },
      { role: "alt1", name: "scnBfTplExternalFactorAlt1Name", kind: "external_factor", desc: "scnBfTplExternalFactorAlt1Desc",
        params: { external_factors: ["oil", "raw"], algorithms: ["AR", "HW", "PR", "XGB", "RF"], influence_threshold: 0.7 } },
      { role: "alt2", name: "scnBfTplExternalFactorAlt2Name", kind: "external_factor", desc: "scnBfTplExternalFactorAlt2Desc",
        params: { external_factors: ["oil", "raw", "fx"], algorithms: ["AR", "HW", "PR", "XGB", "RF"], influence_threshold: 0.6 } },
    ],
  },
  {
    id: "scn_bf_003",
    industry: "bfTplIndustryMarketingSignal",
    name: "bfTpl003Name",
    useCase: "bfTpl003UseCase",
    desc: "bfTpl003Desc",
    tags: ["scnTagMarketingSignal", "scnTagDemandCorrection"],
    scenarios: [
      { role: "base", name: "scnBfTplMarketingSignalBaseName", kind: "marketing_signal", desc: "scnBfTplMarketingSignalBaseDesc",
        params: { algorithms: ["AR", "HW", "PR", "XGB", "RF"], signal_scope: "none" } },
      { role: "alt1", name: "scnBfTplMarketingSignalAlt1Name", kind: "marketing_signal", desc: "scnBfTplMarketingSignalAlt1Desc",
        params: { algorithms: ["AR", "HW", "PR", "XGB", "RF"], signal_scope: "partial" } },
      { role: "alt2", name: "scnBfTplMarketingSignalAlt2Name", kind: "marketing_signal", desc: "scnBfTplMarketingSignalAlt2Desc",
        params: { algorithms: ["AR", "HW", "PR", "XGB", "RF"], signal_scope: "all" } },
    ],
  },
];

// ── BF 시나리오별 파라미터 위젯 (공통 EditableScenarioCards 가 시나리오마다 호출) ──────
// scenario.kind 로 분기 → 계절성 시나리오엔 외부 인자 필드를 노출하지 않는다.
// 모델 선택은 bfSettings 처럼 개별 알고리즘 다중 선택(칩 토글).
// 엔진이 지원하는 BF 모델 — 시나리오별로 selected/unselected 로 포함/제외 (전역 비활성화 없음).
// (계절성 시나리오의 "이동평균·TimesFM 제외"는 해당 시나리오에서 미선택으로 표현)
// BF Settings 확장 파라미터 옵션 (2026-08-13) — 모델 선정 지표 / 이상치 제거 유무.
// 값은 문자열로 통일(select 요소·params JSON 일관성) — 백엔드가 bool/지표로 해석.
// BF Settings(BfSettings.js) 와 동일 옵션·표기 재사용 — 지표 목록/순서(code-util bfMetric),
// 이상치 방법은 설정 화면과 같은 영문 리터럴 3종(zero 는 설정 화면에 없어 제외).
const BF_METRIC_OPTS  = [{ v: "MAPE", l: "MAPE" }, { v: "WAPE", l: "WAPE" }, { v: "RMSE", l: "RMSE" }];
const BF_OUTLIER_OPTS = [{ v: "true", l: "bfOptUse" }, { v: "false", l: "bfOptNotUse" }];
const BF_OUTLIER_METHOD_OPTS = [
  { v: "interpolate", l: "Linear interpolation" }, { v: "ffill", l: "Forward fill" },
  { v: "bfill", l: "Back fill" }];
const BF_ALGO_OPTS = [
  { v: "AR", l: "ARIMA" }, { v: "HW", l: "Holt-Winters" }, { v: "PR", l: "Prophet" },
  { v: "XGB", l: "XGBoost" }, { v: "RF", l: "RandomForest" },
  { v: "SMA", l: "SMA" }, { v: "ZSMA", l: "ZSMA" },
  { v: "ZBASE", l: "Baseline" }, { v: "CRST", l: "Croston" }, { v: "ZTFM", l: "TimesFM" },
];
// 기본 인자(항상 칩으로 노출) — l 값은 i18n 키 문자열, 소비처(컴포넌트)에서 t()로 변환.
const BF_FACTOR_OPTS = [
  { v: "oil", l: "bfFactorOilWtiLabel" }, { v: "raw", l: "bfFactorRawMaterialLabel" },
  { v: "fx", l: "exchangeRate" },
  // cci(소비자신뢰지수): 수집 API 부재로 숨김. API 확보 시 아래 줄 복원.
  // { v: "cci", l: "소비자신뢰지수" },
];
// 카탈로그 인자(검색으로 추가) — 백엔드 factor_source FACTOR_KEY_MAP 과 키 일치. l 값은 i18n 키 문자열.
const BF_FACTOR_OPTS_EXTRA = [
  { v: "rate",  l: "bfFactorUs10yTreasuryLabel" }, { v: "kospi", l: "bfFactorKospiLabel" },
  { v: "sp500", l: "S&P500" },          { v: "dxy",   l: "bfFactorDxyLabel" },
  { v: "vix",   l: "bfFactorVixLabel" },  { v: "gold",  l: "bfFactorGoldLabel" },
  { v: "gas",   l: "bfFactorNaturalGasLabel" },          { v: "brent", l: "bfFactorBrentLabel" },
  { v: "corn",  l: "bfFactorCornLabel" },            { v: "wheat", l: "bfFactorWheatLabel" },
  { v: "jpy",   l: "bfFactorJpyKrwLabel" },        { v: "cny",   l: "bfFactorCnyKrwLabel" },
];
// 라벨 조회용 전체 목록
const BF_FACTOR_OPTS_ALL = [...BF_FACTOR_OPTS, ...BF_FACTOR_OPTS_EXTRA];
// l 값은 i18n 키 문자열 — 소비처(컴포넌트)에서 t()로 변환.
// 마케팅 신호 적용 범위 — 접힌 행에 현재 선택을 보여줄 때 쓰는 라벨 맵.
//   (bfSettings 스키마(paramKey/options)와 달리 이 파일 위젯들은 {v,l} 형태를 쓴다)
const BF_SIGNAL_SCOPE_OPTS = [
  { v: "none",    l: "bfSignalScopeNoneLabel" },
  { v: "partial", l: "bfSignalScopePartialLabel" },
  { v: "all",     l: "bfSignalScopeAllLabel" },
];

// 값 종속 폴백 — 서버 param_defs 의 depends_on 이 없을 때 쓰는 종전 조건.
// true 로 폴백하면 이상치를 끈 상태에서도 감도·방법 행이 남아 적용되지 않는 값을 편집하게 된다.
const _DEPENDS_FALLBACK = { outlier_sensitivity: ["remove_outlier", "true"],
                            outlier_method:      ["remove_outlier", "true"] };

const BF_TARGET_OPTS = [
  { v: "all", l: "all" }, { v: "seasonal", l: "bfSeasonalGroupLabel" }, { v: "classA", l: "bfClassAItemLabel" },
];

// 서버 param_defs -> BF_MODULE.params 파생.
// 파라미터 정의의 정본은 백엔드 PARAM_CATALOG["BF"] 이고 이 파일 상수는 응답 전 폴백이다.
// 배열을 비우지 않는 이유는 isParamDefsMod(!mod.params?.length)가 켜지면
//   챗 set_param 이 params 대신 overrides 에 써서 BF 엔진이 값을 못 읽기 때문이다.
// format 은 함수라 서버가 줄 수 없다 — type 으로 판단해 여기서 만든다.
function bfParamsFromDefs(defs) {
  if (!Array.isArray(defs) || !defs.length) return null;
  return defs.map(d => {
    const opts = (d.options || []).map(o => ({ value: o.value, label: o.label_key || o.label }));
    const p = { paramKey: d.param_key, label: d.label_key || d.label };
    if (d.type === "select" || d.type === "number") p.type = d.type;
    if (d.type === "multi") p.type = "multi";
    if (opts.length) p.options = opts;
    // 배열·객체형은 카드 전용 위젯이 그리지만, 공용 소비처(커스텀 저장 팝업·비교 팝오버)는
    //   format 이 없으면 "AR,HW" 나 [object Object] 를 그대로 노출한다.
    if (d.type === "multi") {
      p.format = (v) => (Array.isArray(v) ? v.map(x => _bfLbl(opts.map(o => ({ v: o.value, l: o.label })), x)).join(" · ") : null);
    } else if (d.type === "factor") {
      p.format = (v, t) => (v && typeof v === "object" && !Array.isArray(v)
        ? Object.entries(v).map(([k, cfg]) => `${_bfLbl(bfFactorGroups().all, k)} ${
            t(FACTOR_METHOD_LABEL[cfg?.method] || FACTOR_METHOD_LABEL[FACTOR_METHOD_DEFAULT])}`).join(" · ")
        : null);
    } else if (d.type === "signal") {
      p.format = (v, t) => t(Array.isArray(v) && v.length ? "paramToggleOnLabel" : "paramToggleOffLabel");
    }
    return p;
  });
}

// 서버 param_defs 를 param_key 로 찾는다. 카드 위젯이 옵션·힌트·종속 조건을 여기서 읽는다.
// 응답 전이거나 조회 실패면 null 이라 호출부가 이 파일 상수로 폴백한다.
function bfDef(key) {
  return (BF_MODULE._paramDefs || []).find(d => d.param_key === key) || null;
}

// 외부 인자 목록을 갈래별로. primary 는 항상 칩으로 걸고 extra 는 검색으로 추가한다.
// 갈래를 정하는 것도 서버 정의(primary_options)이고, 없으면 이 파일 상수로 폴백한다.
function bfFactorGroups() {
  const d = bfDef("external_factors");
  if (!d?.options?.length) {
    return { primary: BF_FACTOR_OPTS, extra: BF_FACTOR_OPTS_EXTRA, all: BF_FACTOR_OPTS_ALL };
  }
  const all = d.options.map(o => ({ v: o.value, l: o.label_key || o.label }));
  const primaryKeys = Array.isArray(d.primary_options) ? d.primary_options : [];
  // primary_options 가 비면 전부 검색 대상으로 두지 않는다 — 칩이 하나도 없으면 인자를 못 찾는다.
  if (!primaryKeys.length) return { primary: all, extra: [], all };
  return { primary: all.filter(o => primaryKeys.includes(o.v)),
           extra:   all.filter(o => !primaryKeys.includes(o.v)), all };
}

// 서버 정의의 선택지를 카드 위젯이 쓰는 {v,l} 형태로. 없으면 폴백 상수를 그대로 쓴다.
function bfOpts(key, fallback) {
  const d = bfDef(key);
  return d?.options?.length
    ? d.options.map(o => ({ v: o.value, l: o.label_key || o.label }))
    : fallback;
}

// 승인된 마케팅 신호를 반응형으로 로드 (BF_MODULE._approvedSignals는 mutable이라 카드가 먼저 렌더되면
//   0건으로 굳음 → 카드가 직접 fetch). 요청은 모듈 단위로 1회만(_bfSignalsPromise 캐시).
let _bfSignalsCache = null;
let _bfSignalsPromise = null;
function useApprovedSignals() {
  const seed = (BF_MODULE._approvedSignals && BF_MODULE._approvedSignals.length)
    ? BF_MODULE._approvedSignals : (_bfSignalsCache || []);
  const [sigs, setSigs] = useState(seed);
  useEffect(() => {
    if (BF_MODULE._approvedSignals && BF_MODULE._approvedSignals.length) {
      setSigs(BF_MODULE._approvedSignals);
      return;
    }
    if (!_bfSignalsPromise) {
      _bfSignalsPromise = scenarioService.getBfInitialState()
        .then(r => {
          const s = r?.approved_signals || [];
          BF_MODULE._approvedSignals = s;
          _bfSignalsCache = s;
          return s;
        })
        .catch(() => []);
    }
    let alive = true;
    _bfSignalsPromise.then(s => { if (alive) setSigs(s || []); });
    return () => { alive = false; };
  }, []);
  return sigs;
}

// 마케팅 신호 기반 인자 시나리오 카드 본문 — 신호 on/off 토글 (적용 모델 RF/XGB 는 예측 알고리즘에 자동 반영).
//   선택 신호 = params.signal_ids (명시) ; 미지정 시 템플릿 signal_scope(none/partial/all)로 기본값 도출.
function BfMarketingFields({ p, disabled, onParamChange, renderModels, lockSignals }) {
  const { t } = useTranslation();
  const approved = useApprovedSignals();
  const sorted = approved.slice()
    .sort((a, b) => Math.abs(Number(b.demand_impact_pct || 0)) - Math.abs(Number(a.demand_impact_pct || 0)));
  const scope = p.signal_scope || "none";   // 기본 off — 사용자가 켤 때만 반영
  const explicit = Array.isArray(p.signal_ids) ? p.signal_ids : null;
  // "일부" = 영향 큰 상위 절반(올림). 신호 2건이면 1건, 전체는 2건. (전체 미만 보장)
  const partialCount = sorted.length < 2 ? sorted.length : Math.ceil(sorted.length / 2);
  const defaultIds = scope === "none" ? []
    : scope === "partial" ? sorted.slice(0, partialCount).map(s => s.id)
      : sorted.map(s => s.id);
  const enabled = explicit ?? defaultIds;
  const onCount = sorted.filter(s => enabled.includes(s.id)).length;

  const toggle = (id) => {
    if (disabled) return;
    onParamChange("signal_ids", enabled.includes(id) ? enabled.filter(x => x !== id) : [...enabled, id]);
  };
  const setAll = (on) => { if (!disabled) onParamChange("signal_ids", on ? sorted.map(s => s.id) : []); };

  // base(현행)는 신호 미반영이 정의 → 토글 없이 안내만 표시, 모델 선택만 노출.
  if (lockSignals) {
    return (
      <Stack spacing={0.8}>
        <Box>
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3, mb: 0.3 }}>{t("bfAppliedMarketSignalLabel")}</Typography>
          <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>{t("bfSignalNotAppliedLockedDesc")}</Typography>
        </Box>
        {renderModels()}
        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
          {t("bfCurrentIsBaselineDesc")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.8}>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.3 }}>
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>
            {t("bfAppliedMarketSignalLabel")} {sorted.length ? `(${onCount}/${sorted.length}${t("case")})` : ""}
          </Typography>
          {!disabled && sorted.length > 0 && (
            <Box sx={{ ml: "auto", display: "flex", gap: 0.6, alignItems: "center" }}>
              <Typography onClick={() => setAll(true)}
                sx={{ fontSize: FONT.MICRO, color: C.acc, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>{t("bfTurnAllOnLabel")}</Typography>
              <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>·</Typography>
              <Typography onClick={() => setAll(false)}
                sx={{ fontSize: FONT.MICRO, color: C.text3, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>{t("bfTurnAllOffLabel")}</Typography>
            </Box>
          )}
        </Box>
        {sorted.length === 0 ? (
          <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>{t("bfNoApprovedSignalDesc")}</Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {sorted.map((s, i) => {
              const on = enabled.includes(s.id);
              const d = Number(s.demand_impact_pct || 0);
              return (
                <Tooltip key={s.id ?? i} title={`${s.title || ""}${on ? t("bfSignalAppliedClickToTurnOffDesc") : t("bfSignalNotAppliedClickToTurnOnDesc")}`} placement="top" arrow>
                  <Chip size="small" onClick={disabled ? undefined : () => toggle(s.id)}
                    label={`${on ? "" : ""}${s.title || s.keyword || t("bfSignalOrdinalLabel", { n: i + 1 })} (${d > 0 ? "+" : ""}${d.toFixed(0)}%)`}
                    sx={{ height: 22, fontSize: FONT.CAPTION, fontWeight: on ? 700 : 500,
                      cursor: disabled ? "default" : "pointer", maxWidth: 240,
                      bgcolor: on ? C.acc : C.bg2, color: on ? "#fff" : C.text2,
                      border: `1px solid ${on ? C.acc : C.border}`, opacity: disabled && !on ? 0.55 : 1 }} />
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>
      {renderModels()}
      <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
        {t("bfEnabledSignalAppliedNote")}
      </Typography>
    </Stack>
  );
}

// ── 외부 인자 미리보기 + 미래 적용 방법 (시나리오 구성 단계) ──────────────────
const _bfFactorCache = {};   // factors-csv → {key: detail}
// l/값은 i18n 키 문자열 — 소비처(컴포넌트)에서 t()로 변환.
const FACTOR_METHOD_OPTS  = [{ v: "forecast", l: "bfMethodForecastLabel" }, { v: "last", l: "bfMethodLastLabel" }, { v: "ma", l: "movingAverage" }, { v: "manual", l: "bfMethodManualLabel" }];
const FACTOR_METHOD_LABEL = { forecast: "bfMethodForecastLabel", last: "bfMethodLastLabel", ma: "movingAverage", manual: "bfMethodManualLabel" };
const FACTOR_METHOD_DEFAULT = "forecast";   // 미래값 기본 = 예측(서버 제공). 예측값 없으면 이동평균 폴백.
const FACTOR_MA_DEFAULT = 3;   // 이동평균 기본 개월 수(사용자 변경 가능)

// history([{ds,value}]) + base 예측 버킷(futureDates) + 적용 방법 → 버킷별 미래값.
//   예측 버킷이 과거 실데이터 구간과 겹치면 실값(actual) 사용. 그 외 last/ma/manual.
function _computeFuture(history, futureDates, method, manualVals, window, forecastPts) {
  const dates = Array.isArray(futureDates) ? futureDates : [];
  if (!dates.length) return [];
  const byMonth = {};
  (history || []).forEach(p => { byMonth[String(p.ds).slice(0, 7)] = Number(p.value); });
  // 예측값(서버 제공) — 클라이언트는 TimesFM 을 못 돌리므로 백엔드 forecast 배열을 사용.
  const fcByMonth = {};
  (forecastPts || []).forEach(p => { fcByMonth[String(p.ds).slice(0, 7)] = Number(p.value); });
  const nums = (history || []).map(p => Number(p.value)).filter(v => !isNaN(v));
  const last = nums.length ? nums[nums.length - 1] : null;
  const w = Math.max(1, Math.min(Number(window) || FACTOR_MA_DEFAULT, nums.length));
  const ma = nums.length ? nums.slice(-w).reduce((a, b) => a + b, 0) / w : last;
  return dates.map((bd, i) => {
    const m = String(bd).slice(0, 7);
    const actual = byMonth[m];
    const mv = manualVals?.[i];
    if (method === "manual" && mv !== "" && mv != null && !isNaN(Number(mv))) return { ds: bd, value: Number(mv), src: "manual" };
    if (actual != null && !isNaN(actual)) return { ds: bd, value: actual, src: "actual" };
    if (method === "forecast") {
      const fv = fcByMonth[m];
      if (fv != null && !isNaN(fv)) return { ds: bd, value: Math.round(fv * 100) / 100, src: "forecast" };
      if (ma != null) return { ds: bd, value: Math.round(ma * 100) / 100, src: "projected" };  // 예측 불가 → 이동평균 폴백
    }
    if (method === "ma" && ma != null) return { ds: bd, value: Math.round(ma * 100) / 100, src: "projected" };
    return { ds: bd, value: last, src: "projected" };
  });
}

function BfExternalFactorFields({ selected, factorFuture, accent, disabled, onSetFactors, onSetFutureCfg,
                                  factorOpts }) {
  const { t } = useTranslation();
  // 목록의 정본은 서버 param_defs 다. 호출부가 안 넘기면(테스트 등) 여기서 직접 집는다.
  const { primary: fPrimary, extra: fExtra, all: fAll } = factorOpts || bfFactorGroups();
  const [preview, setPreview] = useState({});
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const sel = Array.isArray(selected) ? selected : [];
  const csv = [...sel].sort().join(",");

  useEffect(() => {
    if (!csv) { setPreview({}); return; }
    const cached = _bfFactorCache[csv];
    if (cached) { setPreview(cached.map); setPeriod(cached.period); return; }
    let alive = true;
    setLoading(true);
    scenarioService.getBfFactorPreview(sel)
      .then(res => {
        const map = {};
        (res?.factors || []).forEach(f => { map[f.key] = f; });
        _bfFactorCache[csv] = { map, period: res?.period || null };
        if (alive) { setPreview(map); setPeriod(res?.period || null); }
      })
      .catch(() => { /* 미리보기 실패는 조용히 — 실행 시 서버가 재수집 */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [csv]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (v) => { if (!disabled) onSetFactors(sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v]); };
  const fmtV = (v) => v == null ? "-" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const fdates = period?.dates || [];
  const isWeek = (period?.bucket_unit || "month").toLowerCase().startsWith("w");
  const isDay  = (period?.bucket_unit || "month").toLowerCase().startsWith("d");
  const lblDate = (ds) => (isWeek || isDay) ? String(ds).slice(5) : String(ds).slice(0, 7);  // 주/일=MM-DD, 월=YYYY-MM
  const periodTxt = period?.start_date
    ? t("bfPeriodFromLabel", { start: period.start_date.slice(0, 7), n: period.buckets, unit: isWeek ? t("cycleWeek") : isDay ? t("cycleDay") : t("months") }) : "";

  return (
    <Box>
      <Typography sx={{ fontSize: FONT.BODY, color: C.text3, mb: 0.3 }}>
        {t("bfExternalFactorLabel")} {periodTxt && <Typography component="span" sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{t("bfFutureRangeNote", { periodTxt })}</Typography>}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 0.8 }}>
        {/* 기본 3개 칩 + 검색으로 추가된(선택된) 카탈로그 인자 칩 */}
        {[...fPrimary, ...fExtra.filter(o => sel.includes(o.v))].map(o => {
          const on = sel.includes(o.v);
          return <Chip key={o.v} label={t(o.l)} size="small" disabled={disabled}
            onClick={disabled ? undefined : () => toggle(o.v)}
            onDelete={disabled || !on ? undefined : () => toggle(o.v)}
            sx={{ height: 22, fontSize: FONT.CAPTION, fontWeight: on ? 700 : 500,
              cursor: disabled ? "default" : "pointer",
              bgcolor: on ? accent : C.bg2, color: on ? "#fff" : C.text2,
              border: `1px solid ${on ? accent : C.border}`,
              "& .MuiChip-deleteIcon": { color: "#fff", fontSize: 14 } }} />;
        })}
        {/* 카탈로그 인자 검색 추가 (미선택 항목만 검색) */}
        {!disabled && fExtra.some(o => !sel.includes(o.v)) && (
          <Autocomplete
            key={`add-${sel.length}`}   // 추가 후 입력 초기화
            size="small"
            openOnFocus
            options={fExtra.filter(o => !sel.includes(o.v))}
            getOptionLabel={(o) => t(o.l)}
            value={null}
            onChange={(_e, v) => { if (v) onSetFactors([...sel, v.v]); }}
            blurOnSelect clearOnBlur disableClearable
            noOptionsText={t("bfNoResultsLabel")}
            renderInput={(params) => (
              <TextField {...params} placeholder={t("bfAddFactorSearchPlaceholder")} />
            )}
            sx={{ width: 168,
              "& .MuiOutlinedInput-root": { py: "0 !important", minHeight: 24, fontSize: FONT.CAPTION },
              "& .MuiOutlinedInput-root .MuiAutocomplete-input": { p: "1px 4px", fontSize: FONT.CAPTION },
              "& fieldset": { borderColor: C.border, borderStyle: "dashed" } }}
            // 드롭다운(목록) — 컴팩트 + 최대 높이 제한 후 스크롤
            ListboxProps={{ sx: {
              maxHeight: 220, py: 0.5,
              "& .MuiAutocomplete-option": {
                fontSize: FONT.CAPTION, minHeight: "auto", py: "5px", px: "10px",
                borderRadius: "6px", mx: "4px",
                '&[aria-selected="true"], &.Mui-focused': { bgcolor: C.primaryBg },
              },
            } }}
            slotProps={{ paper: { sx: {
              mt: 0.5, borderRadius: "8px", border: `1px solid ${C.border}`,
              boxShadow: "0 6px 20px rgba(17,24,39,0.12)", overflow: "hidden",
            } } }}
          />
        )}
      </Box>
      <Stack spacing={0.6}>
        {sel.map(key => {
          const fd = preview[key];
          const cfg = (factorFuture || {})[key] || {};
          const method = cfg.method || FACTOR_METHOD_DEFAULT;
          const open = openKey === key;
          const labelKey = (fAll.find(o => o.v === key) || {}).l || key;
          const label = t(labelKey);
          const future = fd && fd.points > 0 ? _computeFuture(fd.history, fdates, method, cfg.values, cfg.window, fd.forecast) : [];
          return (
            <Box key={key} sx={{ border: `0.5px solid ${C.border}`, borderRadius: 1, overflow: "hidden" }}>
              <Box onClick={() => setOpenKey(open ? null : key)}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.5, cursor: "pointer",
                  bgcolor: open ? `${accent}0F` : "transparent" }}>
                <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text1, flex: 1 }}>{label}</Typography>
                <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                  {t("bfFutureLabel")}: {t(FACTOR_METHOD_LABEL[method])}{fd ? ` · ${t("dpMonthsValueLabel", { v: fd.points })}` : (loading ? ` · ${t("bfLoadingEllipsis")}` : "")}
                </Typography>
                <Box component="span" sx={{ color: "#9CA3AF", fontSize: 11 }}>{open ? "▾" : "▸"}</Box>
              </Box>
              {open && (
                <Box sx={{ px: 1, py: 0.8 }}>
                  {!fd ? (
                    <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>{loading ? t("bfLoadingDataEllipsis") : t("bfDetailLoadFailedDesc")}</Typography>
                  ) : fd.points === 0 ? (
                    <Typography sx={{ fontSize: FONT.CAPTION, color: C.warning }}>{fd.source}</Typography>
                  ) : (
                    <>
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.6, mb: 0.4 }}>
                        <Chip size="small" label={fd.source}
                          sx={{ height: 19, fontSize: FONT.MICRO, bgcolor: `${C.success}14`, color: C.success, border: `1px solid ${C.success}40` }} />
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{fd.unit} · {fd.date_range}</Typography>
                      </Box>
                      <BfFactorSparkline history={fd.history} future={future} />
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, alignItems: "center", mt: 0.3 }}>
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, whiteSpace: "nowrap" }}>{t("bfFutureRangeColonLabel")}</Typography>
                        {future.map(p => (
                          <Typography key={p.ds} sx={{ fontSize: FONT.MICRO, whiteSpace: "nowrap",
                            color: p.src === "actual" ? C.success : C.text2 }}>
                            {lblDate(p.ds)} <b>{fmtV(p.value)}</b>{p.src === "actual" ? "*" : ""}
                          </Typography>
                        ))}
                      </Box>
                      {future.some(p => p.src === "actual") && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, mt: 0.2 }}>{t("bfActualOverlapNote")}</Typography>
                      )}
                      <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, mt: 0.6, mb: 0.2 }}>{t("bfFutureFactorApplyMethodLabel")}</Typography>
                      <Box sx={{ display: "flex", gap: 0.5, mb: 0.4, alignItems: "center", flexWrap: "wrap" }}>
                        {FACTOR_METHOD_OPTS.map(mo => (
                          <Chip key={mo.v} label={t(mo.l)} size="small" disabled={disabled}
                            onClick={disabled ? undefined : () => onSetFutureCfg(key, { method: mo.v })}
                            sx={{ height: 21, fontSize: FONT.MICRO, fontWeight: method === mo.v ? 700 : 500,
                              cursor: disabled ? "default" : "pointer",
                              bgcolor: method === mo.v ? accent : C.bg2, color: method === mo.v ? "#fff" : C.text2,
                              border: `1px solid ${method === mo.v ? accent : C.border}` }} />
                        ))}
                        {method === "ma" && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, ml: 0.3 }}>
                            <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{t("bfRecentLabel")}</Typography>
                            <Box component="input" type="number" min={1} max={24} disabled={disabled}
                              value={cfg.window ?? FACTOR_MA_DEFAULT}
                              onChange={e => {
                                const n = e.target.value === "" ? "" : Math.max(1, Math.min(24, Number(e.target.value)));
                                onSetFutureCfg(key, { method: "ma", window: n === "" ? undefined : n });
                              }}
                              sx={{ width: 46, p: "2px 5px", border: `0.5px solid ${accent}60`, borderRadius: 0.5,
                                fontSize: FONT.MICRO, fontFamily: "inherit", bgcolor: C.bg2, outline: "none", boxSizing: "border-box" }} />
                            <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{t("bfMonthlyAvgLabel")}</Typography>
                          </Box>
                        )}
                      </Box>
                      {method === "manual" && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {fdates.map((bd, i) => {
                            const def = _computeFuture(fd.history, [bd], "last", null)[0];
                            return (
                              <Box key={bd} sx={{ display: "flex", flexDirection: "column", width: 84 }}>
                                <Typography sx={{ fontSize: FONT.MICRO, color: def?.src === "actual" ? C.success : C.text3 }}>
                                  {lblDate(bd)}{def?.src === "actual" ? "*" : ""}
                                </Typography>
                                <Box component="input" type="number" disabled={disabled}
                                  value={(cfg.values?.[i]) ?? ""} placeholder={String(def?.value ?? "")}
                                  onChange={e => {
                                    const vals = Array.isArray(cfg.values) ? [...cfg.values] : [];
                                    while (vals.length < fdates.length) vals.push("");
                                    vals[i] = e.target.value === "" ? "" : Number(e.target.value);
                                    onSetFutureCfg(key, { method: "manual", values: vals });
                                  }}
                                  sx={{ width: "100%", p: "2px 5px", border: `0.5px solid ${accent}60`, borderRadius: 0.5,
                                    fontSize: FONT.MICRO, fontFamily: "inherit", bgcolor: C.bg2, outline: "none", boxSizing: "border-box" }} />
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function BfCustomScenarioFields({ scenario, accent, disabled, baseParams = null, onParamChange: onParamChangeRaw, onScenarioChange,
                                  view = "all", onStats }) {
  const { t } = useTranslation();
  const kind = scenario.kind || "seasonality";
  const p = scenario.params || {};
  // base(현행) 파라미터 — 변경 안 한 설정은 base 그대로 표시하고, base와 다른 값만 "변경"으로 판정.
  const bp = baseParams || {};
  const onParamChange = (k, v) => onParamChangeRaw(k, v);
  // 시나리오가 명시적으로 설정한 키만 '보유' — 미설정 키는 base 값을 그대로 따른다.
  const has = (k) => Object.prototype.hasOwnProperty.call(p, k) && p[k] != null;
  const effVal = (k) => (has(k) ? p[k] : bp[k]);   // 표시값: 설정값 우선, 없으면 base
  const sameVal = (a, b) => {
    if (Array.isArray(a) || Array.isArray(b)) {
      const aa = Array.isArray(a) ? [...a].map(String).sort() : [];
      const bb = Array.isArray(b) ? [...b].map(String).sort() : [];
      return aa.length === bb.length && aa.every((x, i) => x === bb[i]);
    }
    return (a ?? null) === (b ?? null);
  };
  // "변경" = base 와 다른 값을 가진 설정. (base 카드면 baseParams=null → 항상 false)
  const changedFromBase = (k) => !!baseParams && has(k) && !sameVal(p[k], bp[k]);

  // base(현행 유지·계절성) 카드 자가 보정 — 캐시로 남은 카드가 옛 설정(예: 6버킷)을 갖고 있으면
  // 최신 릴리즈 설정(_currentSettings, 카탈로그 응답 동봉)으로 동기화한다 (2026-07-16).
  // base 는 잠금 카드라 사용자 편집을 덮을 일이 없고, 백테스트의 base 알고리즘으로도 쓰이므로
  // 표시만이 아니라 params 자체를 맞춘다.
  const _cs = BF_MODULE._currentSettings;
  useEffect(() => {
    if (baseParams || (scenario.kind || "seasonality") !== "seasonality") return;   // base 카드만
    if (!_cs || _cs.source === "none" || !onScenarioChange) return;
    const wantHH = _cs.history_horizon;
    const wantAl = (Array.isArray(_cs.algorithms) && _cs.algorithms.length) ? _cs.algorithms : null;
    const needHH = wantHH != null && wantHH !== "" && p.history_horizon !== wantHH;
    const needAl = wantAl && !sameVal(p.algorithms, wantAl);
    if (!needHH && !needAl) return;
    onScenarioChange(sc => ({ ...sc, params: { ...sc.params,
      ...(needHH ? { history_horizon: wantHH } : {}),
      ...(needAl ? { algorithms: [...wantAl] } : {}) } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_cs, baseParams, p.history_horizon, p.algorithms]);

  const lblSx = { fontSize: FONT.BODY, color: C.text3, mb: 0.3 };
  const inputSx = {
    width: "100%", p: "4px 8px", border: `0.5px solid ${accent}60`, borderRadius: 1,
    fontSize: FONT.BODY, fontFamily: "inherit", color: C.text1, bgcolor: C.bg2,
    outline: "none", boxSizing: "border-box", "&:focus": { borderColor: accent },
    "&:disabled": { bgcolor: C.bg3, color: C.text3, cursor: "not-allowed", borderColor: C.border },
  };

  // ── 접힌 행 표시용 값 문자열 + 인라인 입력(라벨 없는 컨트롤) ──────────────────
  // 다른 모듈은 param_defs(타입·옵션)를 백엔드에서 받지만 BF 는 이 파일이 항목을 직접 조립하므로,
  // 항목 정의에 valueText(접힘 표시)와 edit(값 셀 인라인 입력)를 같이 실어 공용 셀에 넘긴다.
  // 되돌리기 — 시나리오 params 에서 그 키를 지운다. 미설정 키는 effVal 이 base 값을 따르므로
  //   "현행(base)으로 복원 + 변경 배지 해제"가 한 번에 된다(별도 기준값을 둘 필요가 없다).
  const revertKeys = (keys) => onScenarioChange?.(sc => {
    const next = { ...(sc.params || {}) };
    (keys || []).forEach(k => { delete next[k]; });
    return { ...sc, params: next };
  });

  const optLabel = (opts, v) => {
    if (v == null || v === "") return null;
    const o = (opts || []).find(x => x.v === v);
    return o ? t(o.l) : String(v);
  };
  const listLabels = (opts, arr) => (Array.isArray(arr) && arr.length)
    ? arr.map(v => optLabel(opts, v) || v).join(", ") : null;
  const selectBare = (pkey, opts) => (
    <Box component="select" value={effVal(pkey) ?? opts[0]?.v} disabled={disabled}
      onChange={e => onParamChange(pkey, e.target.value)}
      sx={{ ...inputSx, ...PARAM_SELECT_W, cursor: disabled ? "not-allowed" : "pointer" }}>
      {opts.map(o => <option key={o.v} value={o.v}>{t(o.l)}</option>)}
    </Box>
  );
  const numberBare = (pkey) => (
    <Box component="input" type="number" value={effVal(pkey) ?? ""} disabled={disabled}
      onChange={e => onParamChange(pkey, e.target.value === "" ? "" : Number(e.target.value))}
      sx={{ ...inputSx, width: PARAM_NUM_INPUT_W }} />
  );

  // 다중 선택(칩 토글) — bfSettings 모델 선택 방식.
  //   onChangeOverride: 토글 결과(next 배열)를 받아 여러 키를 한 번에 갱신할 때 사용(예: 외부인자 선택 시 RF/XGB 동반 체크).
  const multi = (label, pkey, opts, onChangeOverride = null) => {
    const arr = Array.isArray(effVal(pkey)) ? effVal(pkey) : [];
    const toggle = (v) => {
      const next = arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
      if (onChangeOverride) onChangeOverride(next);
      else onParamChange(pkey, next);
    };
    // 옵션에 없는 선택값(릴리즈의 ZSMA/ZBASE/CRST 등)도 칩으로 노출
    const allOpts = [...opts];
    arr.forEach(v => { if (!allOpts.some(o => o.v === v)) allOpts.push({ v, l: v }); });
    return (
      <Box>
        <Typography sx={lblSx}>{label}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {allOpts.map(o => {
            const on = arr.includes(o.v);
            const off = o.disabled || disabled;
            const chip = (
              <Chip key={o.v} label={t(o.l)} size="small" disabled={off}
                onClick={off ? undefined : () => toggle(o.v)}
                sx={{ height: 22, fontSize: FONT.CAPTION, fontWeight: on ? 700 : 500,
                  cursor: off ? "default" : "pointer",
                  bgcolor: o.disabled ? C.neutralBg : on ? accent : C.bg2,
                  color: o.disabled ? C.text3 : on ? "#fff" : C.text2,
                  border: `1px solid ${o.disabled ? C.border : on ? accent : C.border}`,
                  textDecoration: o.disabled ? "line-through" : "none",
                  "&.Mui-disabled": { opacity: 1 } }} />
            );
            return o.disabled && o.note
              ? <Tooltip key={o.v} title={o.note} placement="top" arrow><span>{chip}</span></Tooltip>
              : chip;
          })}
        </Box>
      </Box>
    );
  };
  // ── DP 식 아코디언: 모든 BF 파라미터를 노출. base 카드는 kind 표준 파라미터를 펼치고(배지 없음),
  //    alt 카드는 'base와 달라진 설정'만 펼침 + "변경" 배지. 변경 안 한 설정은 base 값 그대로 표시.
  const PRIMARY = ({
    seasonality:      ["target_group", "algorithms", "history_horizon"],
    external_factor:  ["external_factors", "algorithms", "influence_threshold"],
    marketing_signal: ["marketing", "algorithms"],
  })[kind] || ["target_group", "algorithms", "history_horizon"];
  // 외부 인자 학습은 트리 모델(RF/XGB)만 가능. base 모델 구성은 그대로 두고,
  //   인자를 선택하면 RF/XGB 가 없을 때 자동 체크(추가)한다 → base와 달라지면 '변경'으로 표시됨.
  const onFactorsChange = (nextFactors) => {
    if (!onScenarioChange) { onParamChange("external_factors", nextFactors); return; }
    onScenarioChange(sc => {
      const params = { ...sc.params, external_factors: nextFactors };
      if (nextFactors.length > 0) {
        // 현재 예측 알고리즘(설정값 우선, 없으면 base) 에 RF/XGB 가 없으면 체크
        const cur = Array.isArray(params.algorithms) ? params.algorithms
                  : (Array.isArray(bp.algorithms) ? [...bp.algorithms] : []);
        params.algorithms = [...new Set([...cur, "RF", "XGB"])];
      }
      return { ...sc, params };
    });
  };
  // 마케팅 신호도 외부 인자와 동일하게 처리: 신호가 켜지면(범위≠none 또는 선택 신호 있음)
  //   예측 알고리즘에 RF/XGB 가 없을 때 자동 추가(이미 있으면 변화 없음). 별도 '적용 모델' 필드 폐지.
  const onMarketingParam = (key, val) => {
    if (!onScenarioChange) { onParamChange(key, val); return; }
    onScenarioChange(sc => {
      const params = { ...sc.params, [key]: val };
      const on = Array.isArray(params.signal_ids) ? params.signal_ids.length > 0
               : !!(params.signal_scope && params.signal_scope !== "none");
      if (on) {
        const cur = Array.isArray(params.algorithms) ? params.algorithms
                  : (Array.isArray(bp.algorithms) ? [...bp.algorithms] : []);
        params.algorithms = [...new Set([...cur, "RF", "XGB"])];
      }
      return { ...sc, params };
    });
  };
  // 인자별 미래 적용 방법(forecast/last/ma/manual + 월별 직접입력값)을 params.factor_future 에 저장.
  // 미설정 시 백엔드 기본값(forecast=예측, 불가 시 이동평균 폴백)이 적용된다.
  const setFactorFutureCfg = (key, cfg) => {
    if (!onScenarioChange) return;
    onScenarioChange(sc => {
      const ff = { ...((sc.params || {}).factor_future || {}) };
      ff[key] = { ...(ff[key] || {}), ...cfg };
      return { ...sc, params: { ...sc.params, factor_future: ff } };
    });
  };

  // 마케팅 신호 "켜짐" 판정 — 선택 신호가 있거나 scope 가 미적용이 아닐 때(기본 off).
  const marketingVal = (k) => (has(k) ? p[k] : bp[k]);
  const marketingOn = Array.isArray(marketingVal("signal_ids"))
    ? marketingVal("signal_ids").length > 0
    : !!(marketingVal("signal_scope") && marketingVal("signal_scope") !== "none");
  const baseMarketingOn = Array.isArray(bp.signal_ids)
    ? bp.signal_ids.length > 0
    : !!(bp.signal_scope && bp.signal_scope !== "none");

  const isBaseCard = !baseParams;
  const isP = (k) => PRIMARY.includes(k);
  // 펼침: base 카드는 kind 표준, alt 카드는 base와 달라진 설정. 배지: alt 카드에서 base와 다를 때만.
  const expand = (k) => (isBaseCard ? isP(k) : changedFromBase(k));
  const badge  = (k) => (!isBaseCard && changedFromBase(k));
  // 마케팅은 신호 선택 묶음이라 별도 판정(scope/ids) — base 대비 on/off 변화면 "변경".
  const mktExpand = isBaseCard ? isP("marketing") : (marketingOn !== baseMarketingOn);
  const mktBadge  = !isBaseCard && (marketingOn !== baseMarketingOn);
  // 선택지·힌트·값 종속은 서버 정의를 먼저 본다. 없으면 이 파일 상수로 폴백한다.
  const oTarget  = bfOpts("target_group", BF_TARGET_OPTS);
  const oAlgo    = bfOpts("algorithms", BF_ALGO_OPTS);
  const oMetric  = bfOpts("eval_metric", BF_METRIC_OPTS);
  const oOutlier = bfOpts("remove_outlier", BF_OUTLIER_OPTS);
  const oMethod  = bfOpts("outlier_method", BF_OUTLIER_METHOD_OPTS);
  const oFactorGroups = bfFactorGroups();
  const oFactor  = oFactorGroups.all;
  const oScope   = bfOpts("signal_scope", BF_SIGNAL_SCOPE_OPTS);
  // 행 도움말. 서버가 키를 주면 그것을 쓰고, 없으면 종전 키를 그대로 쓴다.
  const hintOf = (key, fallbackKey) => {
    const k = bfDef(key)?.hint_key || fallbackKey;
    return k ? t(k) : undefined;
  };
  // 값 종속 — 조건이 어긋나면 행을 목록에서 뺀다(이상치를 끄면 감도·방법이 사라진다).
  const dependsMet = (key) => {
    const dep = bfDef(key)?.depends_on;
    const [pk, want] = dep ? [dep.param_key, dep.value] : (_DEPENDS_FALLBACK[key] || []);
    if (!pk) return true;
    return String(effVal(pk)) === String(want);
  };
  const items = [
    { key: "target_group",       label: t("bfTargetGroupLabel"),          primary: expand("target_group"),       badge: badge("target_group"),
      valueText: optLabel(oTarget, effVal("target_group")), revertKeys: ["target_group"],
      edit: () => selectBare("target_group", oTarget) },
    // 예측 알고리즘 = 전체 알고리즘 선택(모든 kind 공통). 외부인자 선택 시 RF/XGB 가 자동 체크됨.
    { key: "algorithms",         label: t("forecastAlgorithmList"),        primary: expand("algorithms"),         badge: badge("algorithms"),
      valueText: listLabels(oAlgo, effVal("algorithms")), revertKeys: ["algorithms"],
      node: () => multi(t("forecastAlgorithmList"), "algorithms", oAlgo) },
    { key: "history_horizon",    label: t("bfErrorEvalBucketCountLabel"),  primary: expand("history_horizon"),    badge: badge("history_horizon"),
      valueText: effVal("history_horizon") != null && effVal("history_horizon") !== ""
        ? String(effVal("history_horizon")) : null,
      hint: hintOf("history_horizon", "bfHistoryHorizonHintDesc"), revertKeys: ["history_horizon"],
      edit: () => numberBare("history_horizon") },
    // ── BF Settings 확장 (2026-08-13): 모델 선정 지표 / 전처리(이상치·학습 기간) ──
    { key: "eval_metric",        label: t("metrics"),            primary: expand("eval_metric"),        badge: badge("eval_metric"),
      valueText: optLabel(oMetric, effVal("eval_metric")),
      hint: hintOf("eval_metric", "bfEvalMetricHintDesc"), revertKeys: ["eval_metric"],
      edit: () => selectBare("eval_metric", oMetric) },
    // 이상치 제거 — 감도·방법은 하위 항목: 미사용이면 행 자체를 숨기고(혼동 방지),
    // 되돌리기 시 하위 값도 함께 초기화한다. 표시 시 라벨에 └ 접두로 종속 관계를 드러낸다.
    { key: "remove_outlier",     label: t("removeOutlier"),         primary: expand("remove_outlier"),     badge: badge("remove_outlier"),
      valueText: optLabel(oOutlier, effVal("remove_outlier") == null ? null : String(effVal("remove_outlier"))),
      hint: hintOf("remove_outlier", "bfRemoveOutlierHintDesc"),
      revertKeys: ["remove_outlier", "outlier_sensitivity", "outlier_method"],
      edit: () => selectBare("remove_outlier", oOutlier) },
    ...(dependsMet("outlier_sensitivity") && dependsMet("outlier_method") ? [
      { key: "outlier_sensitivity", label: `\u2514 ${t("sensitivity")}`, primary: expand("outlier_sensitivity"), badge: badge("outlier_sensitivity"),
        valueText: effVal("outlier_sensitivity") != null && effVal("outlier_sensitivity") !== ""
          ? String(effVal("outlier_sensitivity")) : null,
        hint: hintOf("outlier_sensitivity", "bfOutlierSensitivityHintDesc"), revertKeys: ["outlier_sensitivity"],
        edit: () => numberBare("outlier_sensitivity") },
      { key: "outlier_method",     label: `\u2514 ${t("method")}`,       primary: expand("outlier_method"),     badge: badge("outlier_method"),
        valueText: optLabel(oMethod, effVal("outlier_method")),
        hint: hintOf("outlier_method", "bfOutlierMethodHintDesc"), revertKeys: ["outlier_method"],
        edit: () => selectBare("outlier_method", oMethod) },
    ] : []),
    { key: "train_months",       label: t("trainingPeriod"),           primary: expand("train_months"),       badge: badge("train_months"),
      valueText: effVal("train_months") != null && effVal("train_months") !== ""
        ? String(effVal("train_months")) : null,
      hint: hintOf("train_months", "bfTrainMonthsHintDesc"), revertKeys: ["train_months"],
      edit: () => numberBare("train_months") },
    // 외부 인자: 인자 선택 + 인자별 상세 보기(출처·그래프) + 미래 적용 방법. 선택 시 RF/XGB 동반 체크.
    // ⚠️ kind 로 게이트하지 않는다 — "모든 파라미터는 모든 시나리오 아코디언에 동일"(bf_chat_rules) 원칙.
    //   과거 isFactorKind 게이트 탓에 계절성·마케팅 카드에선 기본 3칩 폴백만 보여
    //   카탈로그 검색·출처/시계열·미래 적용 방법이 사라진 것처럼 보였다(2026-08-13 제거).
    { key: "external_factors",   label: t("bfExternalFactorLabel"),            primary: expand("external_factors"),   badge: badge("external_factors"),
      valueText: listLabels(oFactor, effVal("external_factors")),
      revertKeys: ["external_factors", "factor_future"],   // 미래 적용 방법도 인자에 딸린 설정
      node: () => (
        <BfExternalFactorFields selected={effVal("external_factors")} factorFuture={effVal("factor_future")}
            accent={accent} disabled={disabled} factorOpts={oFactorGroups}
            onSetFactors={onFactorsChange} onSetFutureCfg={setFactorFutureCfg} />) },
    { key: "influence_threshold",label: t("bfFactorInfluenceShortLabel"),   primary: expand("influence_threshold"),badge: badge("influence_threshold"),
      valueText: effVal("influence_threshold") != null && effVal("influence_threshold") !== ""
        ? String(effVal("influence_threshold")) : null,
      hint: hintOf("influence_threshold", "bfInfluenceThresholdHintDesc"), revertKeys: ["influence_threshold"],
      edit: () => numberBare("influence_threshold") },
    // 마케팅 신호: base 대비 on/off 가 달라지면 "변경"·펼침. 변경 안 했으면 base 그대로(보통 off).
    { key: "marketing",          label: t("bfMarketingSignalApplyLabel"),     primary: mktExpand, badge: mktBadge,
      // 적용 여부 판정은 signal_ids(개별 선택) 우선 — signal_scope 만 보면 신호를 직접 골라도
      //   scope 는 "none" 그대로라 "미반영(현행)" 으로 잘못 뜬다(marketingOn 과 같은 기준을 쓴다).
      valueText: (() => {
        const ids = marketingVal("signal_ids");
        if (Array.isArray(ids) && ids.length > 0) {
          return t("paramScopeCountLabel", { count: ids.length, unit: t("bfMarketingSignalNote") });
        }
        return optLabel(oScope, marketingVal("signal_scope") || "none");
      })(),
      revertKeys: ["signal_scope", "signal_ids"],   // 범위·개별 선택이 한 쌍
      node: () => (
        <BfMarketingFields p={has("signal_scope") || has("signal_ids") ? p : bp} disabled={disabled} onParamChange={onMarketingParam}
          lockSignals={scenario.role === "base"} renderModels={() => null} />) },
  ];
  // '적용 모델'(models) 별도 항목 폐지 — 외부 인자와 동일하게 신호 적용 시 RF/XGB 를 예측 알고리즘에 자동 추가한다.

  return <BfParamAccordion items={items} accent={accent} disabled={disabled} onRevert={revertKeys}
           view={view} onStats={onStats} isBase={scenario.role === "base"} />;
}

// BF 파라미터 아코디언 — **단일 펼침**(편집 중인 항목 하나만). DP DpParamDefsFields 와 동형.
//   항목마다 편집 방식이 다르다:
//     - it.edit 보유(대상 제품군·에러율 평가 버킷 수·인자 영향도 임계값) → 다른 모듈처럼 값 셀 인라인 편집
//     - it.node 만 보유(예측 알고리즘 칩·외부 인자 위젯·마케팅 신호) → 펼침 유지.
//       대신 접힌 상태에서도 it.valueText 로 현재 선택을 보여 준다(예전엔 라벨만 남아 뭘 골랐는지 몰랐다).
//   되돌리기 버튼은 없다 — BF 는 "현행 값"이라는 단일 기준이 없어 되돌릴 목표가 정의되지 않는다.
function BfParamAccordion({ items, accent, disabled, view = "all", onStats, isBase, onRevert }) {
  const { t } = useTranslation();
  const [openKey, setOpenKey] = useState(null);
  // 조정만 보기에서 "파라미터 추가"로 끌어온 행 — 조정 판정에 안 걸리므로 고정한다.
  const [pinnedKeys, setPinnedKeys] = useState(() => new Set());
  // 편집을 blur 로 끝낸 직후의 클릭은 무시 — 안 그러면 같은 행을 눌러 끝냈는데 도로 열린다.
  const editGuard = useEditEndGuard();
  const endEdit = () => { editGuard.markEnd(); setOpenKey(null); };
  const toggle = (k) => {
    if (editGuard.justEnded()) return;
    setOpenKey(cur => (cur === k ? null : k));
  };

  const _changedN = items.filter(it => it.badge).length;
  const _totalN   = items.length;
  useEffect(() => {
    if (isBase) return;
    onStats?.({ changed: _changedN, total: _totalN });
    // onStats 는 부모가 매 렌더 새로 만드는 함수라 deps 에서 제외(넣으면 무한 루프).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_changedN, _totalN, isBase]);

  const isVisible = (it) => view === "all" || !!it.badge || pinnedKeys.has(it.key);
  const shown    = items.filter(isVisible);
  const addCands = items.filter(it => !isVisible(it)).map(it => ({ key: it.key, label: it.label }));
  return (
    <Stack spacing={0}>
      {shown.map((it, i) => {
        const isPrimary = it.badge;
        const open = openKey === it.key;
        const inline = !!it.edit;
        const inlineEditing = inline && open && !disabled;
        return (
          <Box key={it.key}
            sx={paramRowSx({ accent, applied: !!isPrimary, last: i === shown.length - 1 })}>
            <Box onClick={() => toggle(it.key)}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, px: PARAM_ROW_PAD_X, py: "6px",
                    height: PARAM_ROW_MIN_H, boxSizing: "border-box", overflow: "hidden",
                    cursor: "pointer", "&:hover": { bgcolor: `${accent}0A` } }}>
              {/* 라벨은 폭을 고정한다 — grow 를 주면 편집 중 값 셀과 남는 공간을 나눠 가져 입력이 밀린다. */}
              <Typography noWrap sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1,
                                       flex: "0 0 36%", minWidth: 0 }}>
                {it.label}
              </Typography>
              <ParamValueCells applied={it.valueText || null} current={it.hint} accent={accent}
                input={inlineEditing ? it.edit() : null}
                onEditEnd={endEdit} />
              {/* base 대비 바뀐 항목은 값 되돌리기(설정 키 삭제 → base 값 복귀),
                  값 없이 추가(pin)만 한 항목은 목록에서 빼기. */}
              {!disabled && (
                <ParamRevertButton accent={accent} hidden={!isPrimary && !pinnedKeys.has(it.key)}
                  title={t(isPrimary ? "revertToKeepCurrent" : "removeParamLabel")}
                  onClick={() => {
                    if (isPrimary) onRevert?.(it.revertKeys);
                    setPinnedKeys(s => { const n = new Set(s); n.delete(it.key); return n; });
                    setOpenKey(cur => (cur === it.key ? null : cur));
                  }} />
              )}
              {/* 펼침 표시는 본문이 따로 있는 행에만. 인라인 행에서도 자리는 남긴다(열 정렬 유지). */}
              <Box component="span" sx={{ flex: "0 0 auto", color: "#9CA3AF", fontSize: 11,
                    width: 14, textAlign: "center", ...(inline ? { visibility: "hidden" } : {}) }}>
                {open ? "▾" : "▸"}
              </Box>
            </Box>
            {open && !inline && (
              <Box sx={{ px: PARAM_ROW_PAD_X, py: 0.8, borderTop: `0.5px dashed ${C.border}` }}>{it.node()}</Box>
            )}
          </Box>
        );
      })}
      {view === "changed" && !disabled && (
        <Box sx={{ mt: 0.6 }}>
          <ScenarioParamAddPicker items={addCands} accent={accent}
            onPick={(k) => { setPinnedKeys(s => { const n = new Set(s); n.add(k); return n; }); setOpenKey(k); }} />
        </Box>
      )}
    </Stack>
  );
}
BF_MODULE.CustomScenarioFields = BfCustomScenarioFields;
// param_defs 를 받더라도 편집 UI 는 BfCustomScenarioFields 가 그린다.
// 이 선언이 없으면 공용 ScenarioParamDefsFields 가 카드를 가로채(useSharedDefs)
//   외부 인자·마케팅 신호 위젯과 RF/XGB 자동 추가 규칙이 함께 사라진다.
BF_MODULE.handlesParamDefs = true;
// 서버 카탈로그를 못 받아도 이 파일 상수(BF_*_OPTS)로 편집 항목을 그릴 수 있다.
// 직접 입력 카드 추가를 막지 않기 위한 표식이다 — 다른 모듈은 param_defs 없이는 아무것도 못 그린다.
BF_MODULE.paramFallbackConstants = true;

// STEP 2 하단: 선택한 표준 시나리오 맥락의 데이터 — 계절성 조합(SCN-BF-001) / 마케팅 신호 선택(SCN-BF-003).
function tplKind(tpl) {
  if (!tpl) return null;
  return tpl.scenarios?.[0]?.kind
    || (tpl.id === "scn_bf_001" ? "seasonality"
      : tpl.id === "scn_bf_002" ? "external_factor"
      : tpl.id === "scn_bf_003" ? "marketing_signal" : null);
}


function BfComposeBottom({ step1Cache }) {
  const { t } = useTranslation();
  // 선택한 템플릿 종류를 BF_MODULE에 기록 → STEP 3 KPI 라벨이 시나리오 종류에 맞게 바뀐다.
  BF_MODULE._activeTplId = step1Cache?.selectedTpl?.id || null;
  const kind = tplKind(step1Cache?.selectedTpl);
  // 마케팅 신호는 각 시나리오 카드 안에서 반영 신호를 직접 보여주므로 하단 카드는 생략.
  if (kind === "marketing_signal") return null;
  if (kind === "seasonality") {
    const combos = BF_MODULE._seasonalCombos || [];
    if (!combos.length) return null;
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: `${C.g2}40`, bgcolor: `${C.g2}06` }}>
        <CardContent sx={{ p: "10px 14px !important" }}>
          <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: C.g2, mb: 0.5 }}>
            {t("bfSeasonalItemComboDesc", { count: combos.length })}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {combos.slice(0, 8).map((s, i) => (
              <Tooltip key={i} title={t("bfSeasonalityScoreTooltip", { score: s.seasonality_score, qty: s.avg_monthly_qty })} placement="top" arrow>
                <Chip size="small" label={`${s.item_nm} (${s.seasonality_score})`}
                  sx={{ height: 20, fontSize: FONT.CAPTION, bgcolor: `${C.g2}14`, color: C.g2, border: `1px solid ${C.g2}30` }} />
              </Tooltip>
            ))}
            {combos.length > 8 && (
              <Chip size="small" label={t("countCasesEtc", { count: combos.length - 8 })}
                sx={{ height: 20, fontSize: FONT.CAPTION, bgcolor: C.neutralBg, color: C.text3 }} />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }
  return null;
}
BF_MODULE.ComposeExtraContentBottom = BfComposeBottom;

// (시나리오별 적용 시장신호 패널은 제거 — KPI 상세 비교의 "반영 신호 수" 셀에서
//  신호 제목 칩으로 표시한다: BF_KPI_E.cellExtra)

// STEP 3 비교 하단 컨테이너: 적용신호 패널 + 백테스트 상세(실제값 vs 예측값).
//   신호 시나리오도 동일 품목 백테스트(신호 후보정)로 비교하므로 항상 백테스트 상세표를 보여준다.
function BfCompareDetail({ aiResult }) {
  return <BfFactorDetailPanel aiResult={aiResult} />;
}
BF_MODULE.CompareDetailContent = BfCompareDetail;

// ── 품목 수준 차이 (CompareItemDiff 공용 위젯) — IP/RP/MP 와 동일한 비교 UX ──────
// 구 '대표품목 백테스트 상세' 피벗표를 대체하며 같은 자리(신호·인자 패널 아래)에 렌더된다
// (afterDetail). BF 는 엔진 plan_ver_id 가 없어 role 기반(roleVersions)으로 저장된
// 백테스트 결과를 서버(/scenario/bf/compare/item-facts)가 request_id 로 조회·비교한다.
// 판정축 = 공통 앵커 WAPE(낮을수록 개선, 1%p 이하 변화 무시 — 2026-08-13 WAPE 통일)
// (LOW ≤15% / MEDIUM ≤30% / HIGH >30%). 실적 합계는 두 버전 동일 기준값(single).
BF_MODULE.itemDiffCfg = {
  roleVersions: true,
  afterDetail: true,
  // locKey 미지정 — 계층 깊이(L1~)는 내부 값이라 컬럼 표시 안 함. 필터는 품목군 트리로 충분.
  itemColKey: "bfItemDiffGroupCol",
  hideRisk: true,                         // 위험도 등급 미사용 — MAPE 단일 축(개선/악화는 %p 로 판단)
  locTreeFilter: true,                    // 필터 = 품목군 계층 트리 검색 드롭다운 (서버 서브트리 필터)
  locFilterAllKey: "bfItemDiffAllGroups",
  // '적용' 배지 제거(2026-08-13) — 비적용 그룹은 base 결과를 그대로 재사용해 항상 '변화 없음'
  // 이므로, 악화/개선 목록에 남는 행은 전부 적용 그룹 → 배지가 정보를 주지 못한다.
  // (데이터 필드 scenario_applied 는 백엔드에 유지 — 챗/리포트 역추적용)
  columns: [
    { key: "mape",       labelKey: "bfItemDiffColMape",   ko: "WAPE(%)",  digits: 1, dir: "down" },
    { key: "pred_qty",   labelKey: "bfItemDiffColPred",   ko: "예측 합계", digits: 0, dir: null },
    { key: "actual_qty", labelKey: "bfItemDiffColActual", ko: "실적 합계", digits: 0, dir: null, single: true },
  ],
  primaryReason: { labelKey: "bfItemDiffReasonMape", ko: "오차", unit: "%p" },
};

// ── 확정 완료 배너 부가 액션: BF 즉시 실행 ─────────────────────────────────────
// 수요계획 프로세스 관리(3100)의 BF 실행과 동일 API(dp-process-mgmt/baselineforecast) —
// 서버가 진행 중 DP 버전(SCENARIO/RUNNING)을 찾아 기준 수요예측을 비동기 실행하고,
// 완료/실패는 알림(Notification)으로 온다. 확정된 새 설정으로 재실행하는 목적이므로
// 기존 예측 결과는 덮어쓴다(overwriteFlg=true). 이미 실행 중이면 서버 가드 메시지 표시.
function BfRunNowAction() {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [snack, setSnack] = useState(null);   // { sev, txt }
  const run = async () => {
    setRunning(true);
    try {
      const res = await dpProcessMgmtService.getParameters({ overwriteFlg: true, removeRef: false });
      const msg = res?.data?.message;
      if (msg) {
        setSnack({ sev: msg.type === "error" ? "error" : "warning", txt: t(msg.message) });
      } else if (res?.status === 204) {
        // 진행 중(SCENARIO/RUNNING) 수요계획 버전 없음 — 프로세스 관리에서 버전 생성 필요
        setSnack({ sev: "warning", txt: t("bfRunNowNoVersion") });
      } else {
        setSnack({ sev: "success", txt: t("bfRunNowStarted") });
      }
    } catch (e) {
      setSnack({ sev: "error", txt: t("MSG_NetworkError") });
    } finally {
      setRunning(false);
    }
  };
  return (
    <>
      <Button variant="outlined" size="small" disabled={running} onClick={run}
        startIcon={running ? <CircularProgress size={12} sx={{ color: C.g1t }} /> : <PlayArrowIcon sx={{ fontSize: 15 }} />}
        sx={{ fontSize: FONT.SUB, py: "5px", px: 1.5, color: C.g1t, borderColor: C.g1s,
              bgcolor: "#fff", textTransform: "none", borderRadius: "8px",
              "&:hover": { borderColor: C.g1, bgcolor: "#fff" } }}>
        {t("bfRunNowLabel")}
      </Button>
      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack?.sev || "info"} onClose={() => setSnack(null)} sx={{ fontSize: FONT.SUB }}>
          {snack?.txt}
        </Alert>
      </Snackbar>
    </>
  );
}
BF_MODULE.ConfirmDoneExtraAction = BfRunNowAction;

// STEP 3 비교 하단: 외부 인자가 '어디서·어떤 값으로'(미래 포함) 들어갔는지 검증 패널.
//   factor_detail = 오케스트레이터가 saas-ai 로 전달한 입력값(출처/과거/미래 유지값).
//   스파크라인 본체는 scenarioStatusUi 의 StatusSparkline 로 승격됨(DP 세그먼트 카드와 공유).
//   여기서는 {value} 포인트 배열 → 숫자 배열 + 과거/미래 경계 인덱스로 변환만 한다.
function BfFactorSparkline({ history, future }) {
  const hist = (history || []).map(p => Number(p.value)).filter(v => !isNaN(v));
  const fut  = (future  || []).map(p => Number(p.value)).filter(v => !isNaN(v));
  return (
    <StatusSparkline values={[...hist, ...fut]}
      splitAt={fut.length ? hist.length - 1 : null} emphasis="head" />
  );
}

function BfFactorDetailPanel({ aiResult }) {
  const { t } = useTranslation();
  const scns = (aiResult?.scenarios || []).filter(s => (s.factor_detail || []).length > 0);
  if (!scns.length) return null;
  const roleClr = { base: C.acc, alt1: C.g1, alt2: C.g2, s1: C.g1, s2: C.g2 };
  const fmtV = (v) => v == null ? "-" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return (
    <Card variant="outlined" sx={{ borderColor: C.border, borderRadius: "12px", overflow: "hidden", mb: 1.5 }}>
      <Box sx={{ p: "10px 16px", bgcolor: C.bg3, borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 16 }}></Typography>
        <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>{t("bfFactorInputTitle")}</Typography>
        <Typography sx={{ fontSize: FONT.BODY, color: C.text3, display: { xs: "none", sm: "block" } }}>
          {t("bfFactorInputNote")}
        </Typography>
      </Box>
      <Box sx={{ p: "10px 14px", display: "flex", flexDirection: "column", gap: 1.2 }}>
        {scns.map(s => {
          const clr = roleClr[s.role] || C.text2;
          return (
            <Box key={s.role}>
              <Chip size="small" label={fieldText(t, s, "name") || s.role}
                sx={{ height: 22, fontSize: FONT.CAPTION, fontWeight: 700, mb: 0.6,
                  bgcolor: `${clr}14`, color: clr, border: `1px solid ${clr}40` }} />
              <Stack spacing={0.8}>
                {(s.factor_detail || []).map(fd => {
                  const last = (fd.history || []).slice(-6);
                  const isYahoo = fd.source_kind === "yahoo";
                  return (
                    <Box key={fd.feature} sx={{ border: `0.5px solid ${C.border}`, borderRadius: 1, p: "8px 10px" }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.6, mb: 0.4 }}>
                        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1 }}>
                          {fieldText(t, fd, "name")}
                        </Typography>
                        {fd.unit && <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                          {fieldText(t, fd, "unit")}
                        </Typography>}
                        <Chip size="small" label={fd.source}
                          sx={{ height: 19, fontSize: FONT.MICRO,
                            bgcolor: isYahoo ? `${C.success}14` : `${C.warning}18`,
                            color: isYahoo ? C.success : C.warning,
                            border: `1px solid ${isYahoo ? C.success : C.warning}40` }} />
                        {fd.points > 0 && (
                          <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>
                            {t("dpMonthsValueLabel", { v: fd.points })} · {fd.date_range}
                          </Typography>
                        )}
                      </Box>
                      {fd.points > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.2 }}>
                          <BfFactorSparkline history={fd.history} future={fd.future} />
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, alignItems: "center", flex: 1, minWidth: 0 }}>
                            {last.map(p => (
                              <Typography key={p.ds} sx={{ fontSize: FONT.MICRO, color: C.text2, whiteSpace: "nowrap" }}>
                                {p.ds.slice(0, 7)} <b>{fmtV(p.value)}</b>
                              </Typography>
                            ))}
                            <Box component="span" sx={{ width: "1px", height: 12, bgcolor: C.border, mx: 0.3 }} />
                            <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, whiteSpace: "nowrap" }}>
                              {t("bfFutureCountNoteLabel", { n: (fd.future || []).length, note: fd.future_note || t("bfKeepLabel") })} <b style={{ color: C.text2 }}>{fmtV((fd.future || [])[0]?.value)}</b>
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: FONT.CAPTION, color: C.warning }}>
                          {t("bfExternalCollectFailedDesc")}
                        </Typography>
                      )}
                      {fd.points > 0 && (
                        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, mt: 0.3 }}>
                          ⓘ {fd.future_note}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
}

// 확정(STEP 4) 적용 대상 선택지 — label/desc 값은 i18n 키 문자열. 공통 화면(AiScenarioPlanning.js)의
//   opt.label/opt.desc 렌더는 원래 raw 였으나, BF 전용 신규 소비 필드라 t() 래핑을 추가했다(최소 래핑).
BF_MODULE.confirmApplyTargets = [
  { value: "scenario_version", label: "bfApplyTargetVersionOnlyLabel", requiresVersion: true,
    desc: "bfApplyTargetVersionOnlyDesc" },
  { value: "bf_settings", label: "bfApplyTargetSettingsLabel",
    desc: "bfApplyTargetSettingsDesc" },
];

// ── 시나리오 구성 탭 2번째 sub-step (BF 전용): AI 추천 외부 인자 선택 + 실제 BF 시뮬 KPI 비교 ──
// 공통 StepScenarioCreate 다음 단계로 StepParamEngine 대신 이 컴포넌트가 주입된다(mod.ComposeRunComponent).
// 통합 계약: { mod, scenarios, cache, onPrev, onComplete(patch?) }
function BfComposeRun({ mod, scenarios, cache, onPrev, onComplete, engineTrigger = 0 }) {
  const { t } = useTranslation();
  const list = (scenarios && scenarios.length ? scenarios : mod.scenarios) || [];
  const requestId       = cache?.request_id || cache?.requestId || null;
  const cachedSelection = cache?.featureSelection ?? null;
  const [running, setRunning] = useState(false);
  const [simError, setSimError] = useState(null);

  // 기본 선택 시나리오: AI 추천(aiRec) > 첫 alt > 첫 항목
  const defaultRole = useMemo(() => {
    if (cachedSelection?.role && list.some(s => s.role === cachedSelection.role)) return cachedSelection.role;
    const rec = typeof mod.aiRec === "number" ? ["base", "alt1", "alt2"][mod.aiRec] : mod.aiRec;
    if (rec && list.some(s => s.role === rec)) return rec;
    return (list.find(s => s.role !== "base") || list[0])?.role || "alt1";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedRole, setSelectedRole] = useState(defaultRole);
  // 인자 체크 상태 { role: Set<idx> } — 기본 전체 체크 (cachedSelection 있으면 복원)
  const [checked, setChecked] = useState(() => {
    const init = {};
    list.forEach(sc => {
      const feats = sc.feature_recommendation?.add_features || [];
      const cachedArr = cachedSelection?.checkedByRole?.[sc.role];
      init[sc.role] = cachedArr ? new Set(cachedArr) : new Set(feats.map((_, i) => i));
    });
    return init;
  });

  const selected   = list.find(s => s.role === selectedRole) || list[0];
  const feats      = selected?.feature_recommendation?.add_features || [];
  const checkedSet = checked[selectedRole] || new Set();
  const isBase     = selectedRole === "base" || feats.length === 0;

  const toggle = useCallback((i) => {
    setChecked(prev => {
      const next = new Set(prev[selectedRole] || []);
      next.has(i) ? next.delete(i) : next.add(i);
      return { ...prev, [selectedRole]: next };
    });
  }, [selectedRole]);

  const handleComplete = async () => {
    const selFeats = feats.filter((_, i) => checkedSet.has(i));
    const selection = {
      role: selectedRole,
      scenario_name: selected?.name,
      checkedByRole: Object.fromEntries(Object.entries(checked).map(([r, s]) => [r, Array.from(s)])),
      selected_features: selFeats,
    };
    // base 시나리오(인자 없음)는 시뮬레이션 없이 진행
    if (isBase || selFeats.length === 0) {
      onComplete?.({ featureSelection: selection, compareResult: null });
      return;
    }
    if (!requestId) {
      setSimError(t("bfRequestIdNotFoundDesc"));
      return;
    }
    setRunning(true); setSimError(null);
    try {
      const compareResult = await scenarioService.compareBfFeatures({
        requestId,
        scenarioId: selectedRole,
        featureNames: selFeats.map(f => f.feature_name).filter(Boolean),
      });
      onComplete?.({ featureSelection: selection, compareResult: compareResult ?? null });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || String(e);
      setSimError(t("bfSimulationFailedDesc", { detail }));
    } finally {
      setRunning(false);
    }
  };

  // AI Agent run_engine 액션 → engineTrigger 증가 시 자동 실행(버튼과 동일)
  useEffect(() => {
    if (engineTrigger > 0 && !running) handleComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineTrigger]);

  // 시나리오 없음 (AI 추천 미실행)
  if (!list.length) {
    return (
      <Stack spacing={1.5}>
        <Paper elevation={0} sx={{ p: "24px", textAlign: "center", bgcolor: C.bg3,
          borderRadius: "12px", border: `1px solid ${C.border}` }}>
          <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1, mb: 0.5 }}>
            {t("bfNoRecommendedScenarioDesc")}
          </Typography>
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>
            {t("bfRunAiFactorRecFirstDesc")}
          </Typography>
        </Paper>
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button size="small" variant="outlined" onClick={onPrev}
            startIcon={<ArrowBackIcon sx={{ fontSize: 12 }} />}
            sx={{ fontSize: FONT.BODY, borderColor: C.border, color: C.text2, textTransform: "none", borderRadius: "7px" }}>
            {t("prev")}
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      {/* 헤더 */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography sx={{ fontSize: FONT.H2, fontWeight: 700, color: C.text1 }}>
          {t("bfAiFactorRecSelectTitle")}
        </Typography>
        <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>
          {t("bfAiFactorRecSelectNote")}
        </Typography>
      </Box>

      {/* ── 상단: 시나리오 선택 카드 (라디오) ── */}
      <Grid container spacing={1} alignItems="stretch">
        {list.map(sc => {
          const active = sc.role === selectedRole;
          const clr = ROLE_COLOR[sc.role] || C.g1;
          const nFeat = (sc.feature_recommendation?.add_features || []).length;
          const isRec = (typeof mod.aiRec === "number" ? ["base","alt1","alt2"][mod.aiRec] : mod.aiRec) === sc.role;
          return (
            <Grid item xs={12} sm={4} key={sc.role} sx={{ display: "flex" }}>
              <Paper elevation={0} onClick={() => setSelectedRole(sc.role)}
                sx={{ flex: 1, p: "10px 12px", cursor: "pointer", borderRadius: "10px",
                  bgcolor: active ? `${clr}0D` : C.bg2,
                  border: `2px solid ${active ? clr : C.border}`,
                  transition: "all .15s", "&:hover": { borderColor: clr } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: "4px" }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: "50%", flexShrink: 0,
                    border: `2px solid ${active ? clr : C.text3}`,
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {active && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: clr }} />}
                  </Box>
                  <Box sx={{ px: "6px", py: "1px", borderRadius: "20px", bgcolor: clr, color: "#fff",
                    fontSize: FONT.CAPTION, fontWeight: 700, flexShrink: 0 }}>{roleLabelText(sc.role, t)}</Box>
                  {isRec && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "2px", px: "5px", py: "1px",
                      borderRadius: "5px", bgcolor: clr, color: "#fff", fontSize: FONT.MICRO, fontWeight: 700 }}>
                      <AutoAwesomeIcon sx={{ fontSize: 10 }} />AI
                    </Box>
                  )}
                  <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, ml: "auto" }}>{t("bfFactorCountLabel", { n: nFeat })}</Typography>
                </Box>
                <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1, lineHeight: 1.3 }}>
                  {sc.name}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* ── 하단: 선택 시나리오의 외부 인자 목록 ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden" }}>
        <Box sx={{ px: "12px", py: "8px", bgcolor: C.bg3, borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", gap: 0.8 }}>
          <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>
            {t("bfScenarioRecommendedFactorLabel", { name: selected?.name })}
          </Typography>
          {!isBase && (
            <Box sx={{ px: "7px", py: "1px", borderRadius: "10px", bgcolor: C.accL,
              border: `1px solid ${C.accS}`, fontSize: FONT.CAPTION, fontWeight: 700, color: C.accT }}>
              {t("bfFactorSelectedCountLabel", { checked: checkedSet.size, total: feats.length })}
            </Box>
          )}
          <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, ml: "auto" }}>
            {t("bfFactorAppliesToModelsPrefix")} <b>RF · XGB</b> {t("bfFactorAppliesToModelsSuffix")}
          </Typography>
        </Box>

        {isBase ? (
          <Box sx={{ p: "20px", textAlign: "center" }}>
            <Typography sx={{ fontSize: FONT.SUB, color: C.text3 }}>
              {t("bfNoExtraFactorForScenarioDesc")}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0} sx={{ p: "8px" }}>
            {feats.map((f, i) => {
              const meta = BF_FEATURE_META[f.feature_type] || { label: f.feature_type, clr: C.text2, bg: C.bg3, bdr: C.border, txt: C.text2 };
              const on = checkedSet.has(i);
              const models  = f.apply_to_models || [];
              const classes = f.target_classes || [];
              const samples = f.sample_values || [];
              const isReal  = f.sample_source === "real";
              return (
                <Box key={i} sx={{ display: "flex", gap: 0.8, p: "10px 8px",
                  borderBottom: i < feats.length - 1 ? `1px solid ${C.border}` : "none",
                  bgcolor: on ? `${meta.clr}06` : "transparent", borderRadius: "6px",
                  transition: "background .15s", opacity: on ? 1 : 0.6 }}>
                  <Checkbox size="small" checked={on} onChange={() => toggle(i)}
                    sx={{ p: "1px", mt: "1px", flexShrink: 0, alignSelf: "flex-start",
                      color: C.border, "&.Mui-checked": { color: meta.clr },
                      "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* 인자명 + 유형 + 출처 */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap", mb: "3px" }}>
                      <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>
                        {f.display_name || t(meta.label)}
                      </Typography>
                      <Box sx={{ px: "6px", py: "1px", borderRadius: "4px", bgcolor: meta.bg,
                        border: `1px solid ${meta.bdr}`, fontSize: FONT.MICRO, fontWeight: 700, color: meta.txt }}>
                        {t(meta.label)}
                      </Box>
                      {f.source_label && (
                        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>· {f.source_label}</Typography>
                      )}
                    </Box>
                    {/* 설명 */}
                    {f.data_detail && (
                      <Box sx={{ mb: "5px" }}>
                        <CollapsibleText text={f.data_detail} lines={2} toggleColor={meta.txt}
                          sx={{ fontSize: FONT.BODY, color: C.text2, lineHeight: 1.55 }} />
                      </Box>
                    )}
                    {/* 적용 모델 / 대상 등급 */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, mb: samples.length ? "5px" : 0 }}>
                      {models.map(m => (
                        <Box key={m} sx={{ px: "6px", py: "1px", borderRadius: "4px", bgcolor: C.accL,
                          border: `1px solid ${C.accS}`, fontSize: FONT.MICRO, fontWeight: 700, color: C.accT }}>
                          {m}
                        </Box>
                      ))}
                      {classes.length > 0 && (
                        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, alignSelf: "center" }}>
                          {t("bfTargetClassLabel", { classes: classes.join(", ") })}
                        </Typography>
                      )}
                    </Box>
                    {/* 샘플값 */}
                    {samples.length > 0 && (
                      <Box sx={{ p: "6px 8px", bgcolor: C.bg3, borderRadius: "6px", border: `1px solid ${C.border}` }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: "3px" }}>
                          <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3 }}>{t("bfSampleValueLabel")}</Typography>
                          {f.value_range && (
                            <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>({f.value_range})</Typography>
                          )}
                          <Box sx={{ px: "5px", py: "1px", borderRadius: "4px", ml: "auto",
                            bgcolor: isReal ? C.g1l : C.g2l, border: `1px solid ${isReal ? C.g1s : C.g2s}`,
                            fontSize: FONT.MICRO, fontWeight: 700, color: isReal ? C.g1t : C.g2t }}>
                            {isReal ? t("bfRealDataBasedLabel") : t("bfAiExampleLabel")}
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                          {samples.map((s, si) => (
                            <Box key={si} sx={{ px: "6px", py: "2px", borderRadius: "4px", bgcolor: C.bg2,
                              border: `1px solid ${C.border}`, fontSize: FONT.CAPTION, fontFamily: "monospace", color: C.text2 }}>
                              {s}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* 구현 가이드 (읽기 전용 정보) */}
      {selected?.feature_recommendation?.implementation_guide && !isBase && (
        <Box sx={{ p: "8px 12px", bgcolor: C.accL, borderRadius: "8px", border: `1px solid ${C.accS}` }}>
          <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.accT, mb: "2px" }}>{t("bfImplementationGuideLabel")}</Typography>
          <Typography sx={{ fontSize: FONT.CAPTION, color: C.accT, opacity: 0.9, lineHeight: 1.6 }}>
            {selected.feature_recommendation.implementation_guide}
          </Typography>
        </Box>
      )}

      {/* 선택 요약 안내 */}
      {!isBase && checkedSet.size === 0 && (
        <Box sx={{ p: "7px 12px", bgcolor: C.g2l, borderRadius: "8px", border: `1px solid ${C.g2s}` }}>
          <Typography sx={{ fontSize: FONT.BODY, color: C.g2t }}>
            {t("bfNoSelectedFactorDesc")}
          </Typography>
        </Box>
      )}

      {/* 시뮬레이션 진행/에러 */}
      {running && (
        <Box sx={{ p: "10px 14px", bgcolor: C.accL, borderRadius: "8px", border: `1px solid ${C.accS}`,
          display: "flex", alignItems: "center", gap: 1.2 }}>
          <CircularProgress size={16} sx={{ color: C.acc }} />
          <Typography sx={{ fontSize: FONT.BODY, color: C.accT, lineHeight: 1.5 }}>
            {t("bfSimulationRunningPrefix")} <b>{t("bfSimulationRunningBold")}</b> {t("bfSimulationRunningSuffix")}
          </Typography>
        </Box>
      )}
      {simError && (
        <Box sx={{ p: "10px 14px", bgcolor: C.g3l, borderRadius: "8px", border: `1px solid ${C.g3}40` }}>
          <Typography sx={{ fontSize: FONT.BODY, color: C.g3t, lineHeight: 1.5 }}>{simError}</Typography>
        </Box>
      )}

      {/* 하단 버튼 */}
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button size="small" variant="outlined" onClick={onPrev} disabled={running}
          startIcon={<ArrowBackIcon sx={{ fontSize: 12 }} />}
          sx={{ fontSize: FONT.BODY, borderColor: C.border, color: C.text2, textTransform: "none", borderRadius: "7px",
            "&:hover": { borderColor: C.acc, color: C.acc, bgcolor: C.accL } }}>
          {t("prev")}
        </Button>
        <Button variant="contained" onClick={handleComplete} disabled={running}
          endIcon={running ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <ArrowForwardIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: FONT.SUB, bgcolor: C.acc, textTransform: "none", borderRadius: "7px",
            "&:hover": { bgcolor: C.accM }, "&.Mui-disabled": { bgcolor: `${C.acc}70`, color: "#fff" } }}>
          {running ? t("bfSimulationRunningEllipsis") : (isBase ? t("bfProceedKeepCurrentLabel") : t("bfProceedSimulateCompareLabel"))}
        </Button>
      </Box>
    </Stack>
  );
}
// 시나리오 구성 탭은 공통 StepParamEngine(3열 파라미터 편집기)을 사용한다 — 다른 모듈과 통일.
// (구 BfComposeRun 인자선택 UI는 ComposeRunComponent 미주입으로 비활성화)
void BfComposeRun;
BF_MODULE.composeNextLabel = "bfComposeNextLabel";

// ── AI 추천 시나리오 → 편집 가능한 표준 파라미터 요약 (화면에 실제 존재하는 값만) ──
// _bfLbl 은 module-level 함수(컴포넌트 아님) — i18n 싱글턴으로 변환하되, BF_ALGO_OPTS 등 고유명사(알고리즘명)는
//   해당 키가 없으면 defaultValue 로 원문이 그대로 반환되어 안전하다.
const _bfLbl = (opts, v) => {
  const key = (opts.find(o => o.v === v) || {}).l || v;
  return i18n.t(key, { defaultValue: key });
};
const BF_SCOPE_LABEL = { none: "bfSignalScopeNoneLabel", partial: "bfSignalScopePartialLabel", all: "bfSignalScopeAllLabel" };
const BF_KIND_LABEL  = { seasonality: "bfKindSeasonalityLabel", external_factor: "bfKindExternalFactorLabel", marketing_signal: "bfKindMarketingSignalLabel" };
function _bfParamSummary(kind, params = {}) {
  const out = [];
  if (kind === "external_factor") {
    const facs = (params.external_factors || []).map(v => _bfLbl(bfFactorGroups().all, v));
    out.push({ label: i18n.t("bfExternalFactorLabel"), value: facs.length ? facs.join(", ") : i18n.t("none") });
    const al = (params.algorithms || []).map(v => _bfLbl(BF_ALGO_OPTS, v));
    if (al.length) out.push({ label: i18n.t("forecastAlgorithmList"), value: al.join(", ") });
    if (params.influence_threshold != null) out.push({ label: i18n.t("bfInfluenceThresholdLabel"), value: String(params.influence_threshold) });
  } else if (kind === "marketing_signal") {
    out.push({ label: i18n.t("bfSignalScopeShortLabel"), value: i18n.t(BF_SCOPE_LABEL[params.signal_scope] || "bfSignalScopeNoneLabel") });
    const al = (params.algorithms || []).map(v => _bfLbl(BF_ALGO_OPTS, v));
    if (al.length) out.push({ label: i18n.t("forecastAlgorithmList"), value: al.join(", ") });
  } else { // seasonality
    if (params.target_group) out.push({ label: i18n.t("bfTargetGroupLabel"), value: _bfLbl(BF_TARGET_OPTS, params.target_group) });
    const al = (params.algorithms || []).map(v => _bfLbl(BF_ALGO_OPTS, v));
    if (al.length) out.push({ label: i18n.t("forecastAlgorithmList"), value: al.join(", ") });
    if (params.history_horizon != null) out.push({ label: i18n.t("bfEvalBucketCountLabel"), value: `${params.history_horizon}${i18n.t("bfCountUnit")}` });
  }
  return out;
}

// ── 시나리오 구성 탭 1번째 sub-step(BF 전용): AI 추천 결과 카드 ──
// 표준 파라미터 요약 + 전문가 코멘트(파라미터로는 못 하는 추가 의견)로 구성한다.
// 공통 StepScenarioCreate 가 mod.AiScenarioCards 주입 시 일반 카드 대신 이 카드를 렌더한다.
function BfAiScenarioCards({ aiResult, mod }) {
  const { t } = useTranslation();
  const RC  = { base: C.acc,  alt1: C.g1,  alt2: C.g2  };
  const RBG = { base: C.accL, alt1: C.g1l, alt2: C.g2l };
  const RBD = { base: C.accS, alt1: C.g1s, alt2: C.g2s };
  const RTX = { base: C.accT, alt1: C.g1t, alt2: C.g2t };
  return (
    <Grid container spacing={1.2} alignItems="stretch">
      {aiResult.scenarios.map(sc => {
        const role  = sc.role;
        const rClr  = RC[role]  || C.g1;
        const rBg   = RBG[role] || C.g1l;
        const rBdr  = RBD[role] || C.g1s;
        const rTxt  = RTX[role] || C.g1t;
        const kind  = sc.kind || "seasonality";
        const summary = _bfParamSummary(kind, sc.params || {});
        const comment = sc.expert_comment || "";
        const isBase = role === "base";
        // 엔진 실행(백테스트) 전이라 "AI 추천" 칩·강조를 붙이지 않는다 (2026-07-16) —
        // 어느 시나리오가 나은지는 비교 단계에서 실측 MAPE 로 정해진다. 여기선 제안 2안을 동등하게 표시.
        return (
          <Grid item xs={12} sm={4} key={role} sx={{ display: "flex" }}>
            <Paper elevation={0} sx={{
              flex: 1, display: "flex", flexDirection: "column",
              p: "14px 14px 12px", bgcolor: C.bg2, borderRadius: "12px",
              border: `2px solid ${rBdr}`,
              borderLeft: `5px solid ${rClr}`,
              boxShadow: "none",
            }}>
              {/* 헤더 */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: "8px", flexWrap: "wrap" }}>
                <Box sx={{ px: "8px", py: "2px", borderRadius: "20px", flexShrink: 0,
                  bgcolor: rClr, color: "#fff", fontSize: FONT.CAPTION, fontWeight: 700 }}>
                  {roleLabelText(role, t)}
                </Box>
                {!isBase && (
                  <Box sx={{ px: "6px", py: "2px", borderRadius: "6px",
                    bgcolor: rBg, color: rTxt, border: `1px solid ${rBdr}`,
                    fontSize: FONT.MICRO, fontWeight: 700 }}>
                    {t(BF_KIND_LABEL[kind] || "bfKindSeasonalityLabel")}
                  </Box>
                )}
              </Box>

              <Typography sx={{ fontSize: FONT.H2, fontWeight: 700, color: C.text1, lineHeight: 1.3, mb: "6px" }}>
                {sc.name}
              </Typography>

              {/* 예상 MAPE 박스 제거(2026-07-15) — LLM 추정치라 오해 소지. 실제 MAPE 비교는
                  엔진 실행 후 비교 단계에서 백테스트 실측으로 표시된다. */}

              {/* 설명 */}
              <Box sx={{ mb: "8px" }}>
                <CollapsibleText text={sc.desc} lines={2} toggleColor={rTxt}
                  sx={{ fontSize: FONT.BODY, color: C.text2, lineHeight: 1.6 }} />
              </Box>

              {/* 설정 요약 (실제 편집 가능한 표준 파라미터) */}
              {summary.length > 0 && (
                <Box sx={{ mb: "8px", display: "flex", flexDirection: "column", gap: "3px" }}>
                  {summary.map((s, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 0.6, alignItems: "baseline" }}>
                      <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, fontWeight: 600, flexShrink: 0, minWidth: 64 }}>
                        {s.label}
                      </Typography>
                      <Typography sx={{ fontSize: FONT.CAPTION, color: C.text1, fontWeight: 600, wordBreak: "keep-all" }}>
                        {s.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* 추천 근거 — base(현행 유지)는 추천 대상이 아니므로 표시하지 않음(2026-07-15) */}
              {!isBase && sc.rationale && (
                <Box sx={{ p: "8px 9px", bgcolor: `${rClr}0D`,
                  borderRadius: "8px", border: `1px solid ${rClr}25`, mb: comment ? "6px" : 0 }}>
                  <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: rTxt, mb: "2px" }}>{t("recReasonLabel")}</Typography>
                  <CollapsibleText text={sc.rationale} lines={3} toggleColor={rTxt}
                    sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.6 }} />
                </Box>
              )}

              {/* 전문가 코멘트 — 파라미터로는 설정할 수 없는 추가 의견 (하단 고정) */}
              {comment && (
                <Box sx={{ mt: "auto", p: "8px 9px", bgcolor: "#FFF7E6",
                  borderRadius: "8px", border: "1px solid #F0C36D" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "3px", mb: "2px" }}>
                    <LightbulbOutlinedIcon sx={{ fontSize: 12, color: "#B7791F" }} />
                    <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: "#B7791F" }}>{t("expertCommentLabel")}</Typography>
                  </Box>
                  <CollapsibleText text={comment} lines={3} toggleColor="#B7791F"
                    sx={{ fontSize: FONT.CAPTION, color: C.text2, lineHeight: 1.6 }} />
                </Box>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
}
BF_MODULE.AiScenarioCards = BfAiScenarioCards;   // (레거시) 공용 AiScenarioRecCards 로 대체됨 — 미참조

// ── 공용 AiScenarioRecCards 카드 본문 위임 (BF) ──────────────────────────────
// 헤더 유형 chip = 인자 kind, 본문 = 표준 파라미터 설정 요약.
BF_MODULE.renderHeaderExtra = (sc, { t, rBg, rTxt, rBdr }) => {
  const kind = sc.kind || "seasonality";
  return (
    <Box sx={{ px: "6px", py: "2px", borderRadius: "6px",
      bgcolor: rBg, color: rTxt, border: `1px solid ${rBdr}`,
      fontSize: FONT.BODY, fontWeight: 700 }}>
      {t(BF_KIND_LABEL[kind] || "bfKindSeasonalityLabel")}
    </Box>
  );
};
BF_MODULE.renderPreviewBody = (sc) => {
  const summary = _bfParamSummary(sc.kind || "seasonality", sc.params || {});
  if (!summary.length) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      {summary.map((s, i) => (
        <Box key={i} sx={{ display: "flex", gap: 0.6, alignItems: "baseline" }}>
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3, fontWeight: 600, flexShrink: 0, minWidth: 64 }}>{s.label}</Typography>
          <Typography sx={{ fontSize: FONT.BODY, color: C.text1, fontWeight: 600, wordBreak: "keep-all" }}>{s.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

// ── 시나리오 비교 탭(BF 전용): 선택 인자 실제 BF 시뮬레이션 결과 비교 뷰 ──
// 공통 StepKpiCompare 가 mod.CompareResultComponent + mod.compareResult 존재 시 이 뷰로 위임한다.
function BfFeatureCompareResult({ mod, compareResult, onPrev, onNext }) {
  const { t } = useTranslation();
  const cr = compareResult;
  const items = cr.items || [];
  const fmtMape = (v) => (v == null ? "—" : `${v}%`);
  const fmtNum = (v) => (v == null ? "—" : Number(v).toLocaleString());
  const futMonths = cr.future_months ?? (items[0]?.future_series || []).length;
  const dpct = cr.future_delta_pct;
  const hasImpact = dpct != null && Math.abs(dpct) > 0.01;
  const impactClr = dpct == null ? C.text3 : dpct > 0 ? C.g1 : C.g3;
  const impactBg  = dpct == null ? C.bg3 : dpct > 0 ? C.g1l : C.g3l;
  const impactBdr = dpct == null ? C.border : dpct > 0 ? C.g1s : C.g3;

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography sx={{ fontSize: FONT.H2, fontWeight: 700, color: C.text1 }}>
          {t("bfActualSimulationResultTitle")}
        </Typography>
        <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>
          {t("bfActualSimulationResultNote", { n: items.length })}
        </Typography>
      </Box>

      {(cr.selected_features?.length > 0) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
          <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, fontWeight: 700 }}>{t("bfAppliedFactorLabel")}</Typography>
          {cr.selected_features.map((f, i) => (
            <Box key={i} sx={{ px: "7px", py: "2px", borderRadius: "4px", bgcolor: C.accL,
              border: `1px solid ${C.accS}`, fontSize: FONT.CAPTION, color: C.accT, fontWeight: 600 }}>{f}</Box>
          ))}
        </Box>
      )}

      {/* ── 섹션 1: 모델 정확도 (과거 백테스트) ── */}
      <Box>
        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1, mb: 0.8 }}>
          {t("bfSection1ForecastModelAccuracyTitle")} <Typography component="span" sx={{ fontSize: FONT.CAPTION, color: C.text3, fontWeight: 400 }}>{t("bfRecentBacktestMapeNote", { n: (items[0]?.backtest_series || []).length })}</Typography>
        </Typography>
        <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 1fr",
            bgcolor: C.bg3, borderBottom: `1px solid ${C.border}`, px: "12px", py: "7px" }}>
            {[t("bfRepresentativeItemLabel"), t("bfModelLabel"), "WAPE"].map((h, i) => (
              <Typography key={i} sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2, textAlign: i === 2 ? "right" : "left" }}>{h}</Typography>
            ))}
          </Box>
          {items.map((it, idx) => (
            <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 1fr",
              px: "12px", py: "8px", borderTop: idx > 0 ? `1px solid ${C.border}` : "none",
              bgcolor: idx % 2 ? `${C.bg3}80` : C.bg2, alignItems: "center" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text1, overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.item_nm || it.item_cd}</Typography>
                <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{it.item_cd}</Typography>
              </Box>
              <Typography sx={{ fontSize: FONT.CAPTION, color: C.text2 }}>{it.model_cd || "-"}</Typography>
              <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1, textAlign: "right" }}>{fmtMape(it.backtest_mape)}</Typography>
            </Box>
          ))}
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 1fr", px: "12px", py: "9px",
            bgcolor: C.bg3, borderTop: `2px solid ${C.border}`, alignItems: "center" }}>
            <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text2 }}>{t("bfAvgMapeLabel")}</Typography>
            <span />
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: C.acc, textAlign: "right" }}>{fmtMape(cr.avg_backtest_mape)}</Typography>
          </Box>
        </Paper>
      </Box>

      {/* ── 섹션 2: 신호 인자 미래 영향 ── */}
      <Box>
        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1, mb: 0.8 }}>
          {t("bfSection2SignalFutureImpactTitle", { n: futMonths })} <Typography component="span" sx={{ fontSize: FONT.CAPTION, color: C.text3, fontWeight: 400 }}>{t("bfBaselineMultiplierEffectNote")}</Typography>
        </Typography>

        <Grid container spacing={1.2} sx={{ mb: 1 }}>
          {[
            { label: t("bfFutureBaselineForecastSumLabel", { n: futMonths }), value: fmtNum(cr.future_baseline_total), clr: C.text2, bg: C.bg3, bdr: C.border },
            { label: t("bfSignalFactorAppliedForecastSumLabel"), value: fmtNum(cr.future_feature_total), clr: impactClr, bg: impactBg, bdr: impactBdr },
            { label: t("bfForecastChangeLabel"), value: dpct == null ? "—" : `${dpct > 0 ? "▲ +" : "▼ "}${Math.abs(dpct)}% (${cr.future_delta_qty > 0 ? "+" : ""}${fmtNum(cr.future_delta_qty)})`, clr: impactClr, bg: impactBg, bdr: impactBdr },
          ].map((c, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Box sx={{ p: "12px 14px", bgcolor: c.bg, borderRadius: "10px", border: `1px solid ${c.bdr}`, textAlign: "center" }}>
                <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, mb: "3px" }}>{c.label}</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: c.clr, lineHeight: 1.1 }}>{c.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 0.9fr",
            bgcolor: C.bg3, borderBottom: `1px solid ${C.border}`, px: "12px", py: "7px" }}>
            {[t("bfRepresentativeItemLabel"), t("bfBaselineSumMonthsLabel", { n: futMonths }), t("bfFactorAppliedSumLabel"), t("bfChangeLabel")].map((h, i) => (
              <Typography key={i} sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2, textAlign: i === 0 ? "left" : "right" }}>{h}</Typography>
            ))}
          </Box>
          {items.map((it, idx) => {
            const b = it.future_baseline_total, f = it.future_feature_total;
            const dp = it.future_delta_pct;
            const up = dp != null && dp > 0;
            return (
              <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 0.9fr",
                px: "12px", py: "8px", borderTop: idx > 0 ? `1px solid ${C.border}` : "none",
                bgcolor: idx % 2 ? `${C.bg3}80` : C.bg2, alignItems: "center" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text1, overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.item_nm || it.item_cd}</Typography>
                  <Typography sx={{ fontSize: FONT.MICRO, color: C.text3 }}>{it.item_cd}</Typography>
                </Box>
                <Typography sx={{ fontSize: FONT.BODY, color: C.text2, textAlign: "right" }}>{fmtNum(b)}</Typography>
                <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1, textAlign: "right" }}>{fmtNum(f)}</Typography>
                <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, textAlign: "right", color: dp == null ? C.text3 : up ? C.g1 : C.g3 }}>
                  {dp == null ? "—" : `${up ? "+" : ""}${dp}%`}
                </Typography>
              </Box>
            );
          })}
        </Paper>

        {!hasImpact && (
          <Box sx={{ mt: 1, p: "7px 12px", bgcolor: C.bg3, borderRadius: "8px", border: `1px solid ${C.border}` }}>
            <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
              {t("bfNoDemandMultiplierImpactDesc")}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ p: "8px 12px", bgcolor: C.accL, borderRadius: "8px", border: `1px solid ${C.accS}` }}>
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.accT, lineHeight: 1.6 }}>
          {t("bfAccuracyVsImpactExplainDesc")}
        </Typography>
      </Box>

      {(cr.errors?.length > 0) && (
        <Box sx={{ p: "8px 12px", bgcolor: C.g2l, borderRadius: "8px", border: `1px solid ${C.g2s}` }}>
          <Typography sx={{ fontSize: FONT.CAPTION, color: C.g2t }}>
            {t("bfPartialItemForecastFailedDesc", { count: cr.errors.length })}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button size="small" variant="outlined" onClick={onPrev}
          startIcon={<ArrowBackIcon sx={{ fontSize: 12 }} />}
          sx={{ fontSize: FONT.BODY, borderColor: C.border, color: C.text2, textTransform: "none", borderRadius: "7px",
            "&:hover": { borderColor: C.acc, color: C.acc, bgcolor: C.accL } }}>
          {t("prev")}
        </Button>
        <Button variant="contained" onClick={() => onNext()}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
          sx={{ fontSize: FONT.SUB, bgcolor: C.acc, textTransform: "none", borderRadius: "7px",
            "&:hover": { bgcolor: C.accM } }}>
          {(mod.step4lbl ? t(mod.step4lbl) : t("confirm"))}{t("toSuffixLabel")}
        </Button>
      </Box>
    </Stack>
  );
}
// 표준 시나리오 비교는 공통 StepKpiCompare(대표품목 MAPE 비교)를 사용 — 다른 모듈과 통일.
// (구 feature 비교 뷰 BfFeatureCompareResult 는 CompareResultComponent 미주입으로 비활성화)
void BfFeatureCompareResult;

// ── 마케팅 신호 사이드 패널 ────────────────────────────────────────────────
// 구 상시 드로어 컴포넌트 — 마케팅 신호는 SCN-BF-003 각 시나리오 카드 안에서 반영 신호로 표시됨.
// eslint-disable-next-line no-unused-vars
function MarketingSignalPanel({ selectedIds, onToggle }) {
  const { t } = useTranslation();
  const [signals,  setSignals]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    scenarioService.getBfApprovedSignals()
      .then(r => setSignals(r.signals || []))
      .catch(e => setError(e?.response?.data?.detail || e.message || t("bfSignalQueryErrorDesc")))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const selectedCount = selectedIds.length;
  const upCount   = signals.filter(s => s.direction === "up").length;
  const downCount = signals.filter(s => s.direction === "down").length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* 헤더 */}
      <Box sx={{ p: "10px 12px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <CampaignIcon sx={{ fontSize: 16, color: C.primary }} />
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle2" fontWeight={700} fontSize={12} lineHeight={1.2}>
              {t("bfMarketingSignalLinkTitle")}
            </Typography>
            <Typography variant="caption" fontSize={9.5} color="text.secondary">
              {t("bfSelectSignalToAddAsFeatureDesc")}
            </Typography>
          </Box>
          <Tooltip title={t("refresh")}>
            <IconButton size="small" onClick={load} disabled={loading} sx={{ p: "3px" }}>
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* 요약 통계 */}
        {signals.length > 0 && (
          <Stack direction="row" spacing={1} mt={0.8} flexWrap="wrap" useFlexGap>
            <Chip
              label={t("bfTotalSignalCountLabel", { count: signals.length })}
              size="small"
              sx={{ height: 20, fontSize: FONT.CAPTION, fontWeight: 700,
                bgcolor: C.primaryBg, color: C.primary }}
            />
            {upCount > 0 && (
              <Chip
                icon={<TrendingUpIcon sx={{ fontSize: "11px !important", color: `${C.success} !important` }} />}
                label={`${t("bfDemandUpLabel")} ${upCount}`}
                size="small"
                sx={{ height: 20, fontSize: FONT.CAPTION, fontWeight: 600,
                  bgcolor: C.successBg, color: C.success }}
              />
            )}
            {downCount > 0 && (
              <Chip
                icon={<TrendingDownIcon sx={{ fontSize: "11px !important", color: `${C.danger} !important` }} />}
                label={`${t("bfDemandDownLabel")} ${downCount}`}
                size="small"
                sx={{ height: 20, fontSize: FONT.CAPTION, fontWeight: 600,
                  bgcolor: C.dangerBg, color: C.danger }}
              />
            )}
          </Stack>
        )}

        {/* 선택 상태 */}
        {selectedCount > 0 && (
          <Box sx={{ mt: 0.8, p: "5px 8px", bgcolor: C.primaryBg,
            borderRadius: 1, border: `1px solid ${C.primaryL}` }}>
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <AutoAwesomeIcon sx={{ fontSize: 12, color: C.primary }} />
              <Typography variant="caption" fontSize={10} fontWeight={700} color={C.primary}>
                {t("bfSignalSelectedAppliedToAiDesc", { count: selectedCount })}
              </Typography>
            </Stack>
          </Box>
        )}
        {selectedCount === 0 && signals.length > 0 && (
          <Typography variant="caption" fontSize={9.5} color="text.disabled"
            display="block" mt={0.6}>
            {t("bfNoSelectionUsesAllApprovedDesc")}
          </Typography>
        )}
      </Box>

      {/* 신호 목록 */}
      <Box sx={{
        flex: 1, overflowY: "auto", p: "8px 10px",
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { bgcolor: `${C.neutral}40`, borderRadius: 2 },
      }}>
        {loading && (
          <Stack alignItems="center" justifyContent="center" height={120} spacing={1}>
            <CircularProgress size={22} sx={{ color: C.primary }} />
            <Typography variant="caption" color="text.secondary">{t("bfSignalQueryingEllipsis")}</Typography>
          </Stack>
        )}
        {error && (
          <Box sx={{ p: "10px 12px", bgcolor: C.dangerBg, borderRadius: 1.5,
            border: `1px solid ${C.danger}30` }}>
            <Typography variant="caption" color={C.danger} fontSize={11}>{error}</Typography>
          </Box>
        )}
        {!loading && !error && signals.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CampaignIcon sx={{ fontSize: 32, color: C.neutralBg, mb: 1 }} />
            <Typography variant="body2" color="text.secondary" fontSize={12}>
              {t("bfNoApprovedMarketSignalDesc")}
            </Typography>
            <Typography variant="caption" color="text.disabled" fontSize={10.5}
              display="block" mt={0.4}>
              {t("bfApproveSignalGuideDesc")}
            </Typography>
          </Box>
        )}
        {!loading && signals.length > 0 && (
          <Stack spacing={0.8}>
            {signals.map(sig => (
              <SignalCard
                key={sig.id}
                signal={sig}
                selected={selectedIds.includes(sig.id)}
                onToggle={onToggle}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* 하단 안내 */}
      {!loading && signals.length > 0 && (
        <Box sx={{ p: "8px 10px", borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <Box sx={{ bgcolor: `${C.teal}08`, borderRadius: 1, p: "6px 9px",
            border: `1px solid ${C.teal}20` }}>
            <Typography variant="caption" fontSize={9.5} color={C.teal} lineHeight={1.65}>
              <b>{t("bfSignalToFeatureConversionTitle")}</b><br/>
              {t("bfSignalConversionDescPart1")} <code>demand_impact_pct</code>{t("bfSignalConversionDescPart2")}<br/>
              {t("bfSignalConversionDescPart3")}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// 표준 시나리오 동적 로딩 (z_ai_scenario 카탈로그 — 기존 하드코딩 templates 대체).
// 공통 StepScenarioCreate 가 mod.fetchTemplates 존재 시 이 결과를 템플릿 카드로 렌더한다.
// display-only: 변형(base/alt1/alt2)은 이름·설명·변경항목(change_items)만 담고 엔진 params 는 비움
// → seedScenarios(param_defs 미보유)가 mod.params 기본값으로 백필, 엔진 바인딩은 모듈별 후속.
// DB override(scope:"all").params 를 시나리오의 편집 params 로 바인딩 — BF 전용.
//   z_ai_scenario.scenarios[].param_overrides[role] → catalog 가 variant.overrides 로 변환.
//   공통 seedScenarios 는 BF param_defs=[] 라 BF_MODULE.params 로 백필하므로, 여기서
//   DB 값(override params)을 params 로 옮기고 kind 도 세팅해 DB를 alt1/alt2 단일 소스로 만든다.
const _bindBfVariant = (v, problemTargets) => {
  const params = { ...(v.params || {}) };
  let kind, memo;
  for (const ov of (v.overrides || [])) {
    if (ov?.scope_type === "all" && ov.params) {
      // kind/desc(메모) 는 params 에서 분리(편집 필드 오염 방지), 나머지는 편집 params 로
      const { kind: k, desc: d, memo: m, models: legacyModels, ...rest } = ov.params;
      if (k) kind = k;
      if (d || m) memo = d || m;
      Object.assign(params, rest);
      // 구 카탈로그 키 "models" → "algorithms" 정규화 (2026-07-16) — 미매핑 시 algorithms 가
      // 비어 base(전체 모델)를 상속 표시해 "외부 인자=트리 모델만" 의도와 어긋난다.
      if (Array.isArray(legacyModels) && legacyModels.length && !params.algorithms) {
        params.algorithms = [...legacyModels];
      }
    }
  }
  // 정제 전 원본 overrides 는 버린다 — BF 편집 정본은 params 하나다(단일 소스).
  //   남겨 두면 overrides 를 우선하는 공용 소비처(flattenScenarioParamsForCustomSave 등)가
  //   사용자 편집이 아닌 DB 원본을, 그것도 걷어낸 kind/desc/models 까지 함께 읽는다.
  //   BF 엔진 경로는 params 만 쓰므로(백엔드 scenario/bf 에 overrides 참조 없음) 영향 없다.
  const { overrides: _dbOverrides, ...rest } = v;
  return {
    ...rest, params,
    ...(kind ? { kind } : {}),
    ...(memo ? { desc: memo } : {}),   // alt 메모를 DB override 에서 (없으면 variant.desc=scenario desc_txt 유지)
    problem_targets: problemTargets,
  };
};

// base(현행 유지) 에 최신 릴리즈 설정 반영 — 계절성 base 만 알고리즘·평가버킷.
const _applyBaseCurrentSettings = (v, tplKind) => {
  const cs = BF_MODULE._currentSettings;
  if (v.role !== "base" || tplKind !== "seasonality" || !cs || cs.source === "none") return v;
  const params = { ...(v.params || {}) };
  if (Array.isArray(cs.algorithms) && cs.algorithms.length) params.algorithms = cs.algorithms.slice();
  if (cs.history_horizon != null && cs.history_horizon !== "") params.history_horizon = cs.history_horizon;
  // BF Settings 확장 현행값 (2026-08-13) — remove_outlier 는 select 일관성 위해 문자열화
  if (cs.remove_outlier != null) params.remove_outlier = String(cs.remove_outlier);
  if (cs.outlier_sensitivity != null && cs.outlier_sensitivity !== "") params.outlier_sensitivity = cs.outlier_sensitivity;
  if (cs.outlier_method) params.outlier_method = String(cs.outlier_method).toLowerCase();
  if (cs.train_months != null && cs.train_months !== "") params.train_months = cs.train_months;
  if (cs.eval_metric) params.eval_metric = String(cs.eval_metric).toUpperCase();
  return {
    ...v, params,
    desc: cs.history_horizon != null ? `최신 릴리즈 설정 그대로, 평가 버킷 ${cs.history_horizon}.` : v.desc,
  };
};

BF_MODULE.fetchTemplates = async () => {
  const res = await scenarioService.getScenarios("BF");
  rememberModuleCatalog(BF_MODULE, res);   // 표준 시나리오 0건이어도 직접 입력 카드가 그려지도록 최상위 카탈로그 보관
  // 파라미터 정의의 정본은 서버다. 카드 위젯이 bfDef/bfOpts 로 읽고, 공용 소비처는 BF_MODULE.params 를 본다.
  if (Array.isArray(res?.param_defs) && res.param_defs.length) {
    BF_MODULE._paramDefs = res.param_defs;
    BF_MODULE.params = bfParamsFromDefs(res.param_defs) || BF_MODULE.params;
  }
  // 현행 설정(최신 릴리즈 BF의 평가 버킷·허용 알고리즘)이 카탈로그 응답에 동봉된다(2026-07-16).
  // 현황 탭(initial-state) 로드 타이밍과 무관하게 base 카드가 항상 실제 설정으로 시드되도록
  // 여기서 먼저 반영한다 (아래 _applyBaseCurrentSettings 가 이 값을 사용).
  if (res?.bf_current_settings && res.bf_current_settings.source !== "none") {
    BF_MODULE._currentSettings = res.bf_current_settings;
    applyBfCurrentSettings(res.bf_current_settings);
  }
  const problemTargets = res?.problem_targets || {};
  return (res?.scenarios || []).map(scn => {
    const variants = (scn.variants || []).map(v => _bindBfVariant(v, problemTargets));
    // kind 는 템플릿 공통 — alt override 에서 발견한 kind 를 base(override 없음)에도 전파
    const tplKind = variants.find(v => v.kind)?.kind;
    return {
      id:          scn.scenario_cd,
      industry:    scn.source_type === "USER" ? "userTagLabel"
                   : (scn.recommended_flg === "Y" ? "recommendedTagLabel" : "standardTagLabel"),
      // 표시용 i18n 키(*_key)를 우선 사용, 없으면 원문 폴백. LLM 은 원문을 그대로 소비.
      name:        scn.scenario_name_key || scn.scenario_name,
      direction:   scn.scenario_direction_key || scn.scenario_direction,
      useCase:     scn.desc_txt_key || scn.desc_txt,
      desc:        scn.desc_txt_key || scn.desc_txt,
      recommended: scn.recommended_flg === "Y",
      scenarios:   variants.map(v => {
        const withKind = tplKind ? { ...v, kind: v.kind || tplKind } : v;
        return _applyBaseCurrentSettings(withKind, withKind.kind || tplKind);
      }),
    };
  });
};

// ── BF 시나리오 플래닝 페이지 (분할 화면) ────────────────────────────────────
const AiScenarioPlanningLazy = React.lazy(() => import("./AiScenarioPlanning"));

export default function AiBfScenarioPlanning({ viewName }) {
  // 마케팅 신호는 "마케팅 신호 기반 인자" 시나리오 선택 시에만 사용 → 상시 드로어 제거.
  return (
    <Box sx={{ height: "100%", overflow: "hidden", bgcolor: "#F8F9FB" }}>
      <React.Suspense fallback={
        <Stack alignItems="center" justifyContent="center" height="100%">
          <CircularProgress size={28} />
        </Stack>
      }>
        <AiScenarioPlanningLazy viewName={viewName} singleModuleCode="BF" />
      </React.Suspense>
    </Box>
  );
}
